class BundleBox {
  constructor(apiUrl, productPlatformId) {
    this._apiUrl = apiUrl;
    this._productPlatformId = productPlatformId;

    this._elements = {
      container: document.querySelector(".skio-bundle-builder__container"),
      boxContainer: document.querySelector(".skio-bundle-builder__products"),
      upsellsContainer: document.querySelector(".skio-bundle-builder__upsells"),
      subscriptionUpsellsContainer: document.querySelector(".skio-bundle-builder__subscription-upsells"),
      remainQuantityBlock: document.querySelector("[data-remain-quantity]"),
      subtotalCompareSumBlock: document.querySelector("[data-subtotal-compare-sum]"),
      subtotalMealPriceBlock: document.querySelector("[data-subtotal-meal]"),
      subtotalSumBlock: document.querySelector("[data-subtotal-sum]"),
      checkoutBlock: document.querySelector(".skio-bundle-builder__checkout-block"),
      checkoutButton: document.querySelector(".skio-bundle-builder__checkout"),
      remainQuantityContainer: document.querySelector(".skio-bundle-builder__note"),
      alertContainer: document.querySelector(".skio-bundle-builder__alert"),
      bundleCart: document.querySelector("[data-cart-items]"),
      totalsContentBlock: document.querySelector(".skio-bundle-builder__totals-content"),
      progressBarContainer: document.querySelector(".skio-bundle-builder__progress-bar-container"),
      progressBarText: document.querySelector(".skio-bundle-builder__progress-bar-text"),
      progressBar: document.querySelector(".skio-bundle-builder__progress-bar"),
      paymentsBlock: document.querySelector(".skio-bundle-builder__payments"),
    };

    this._sticky = {
      container: document.querySelector(".skio-bundle-builder__sticky-bar-container"),
      progressBar: document.querySelector(".skio-bundle-builder__sticky-bar .skio-bundle-builder__progress-bar"),
      progressText: document.querySelector(".skio-bundle-builder__sticky-bar [data-progress-quantity]"),
      note: document.querySelector(".skio-bundle-builder__sticky-bar .btn-progress"),
      noteBlock: document.querySelector(".skio-bundle-builder__sticky-bar .skio-bundle-builder__note"),
      progressBarText: document.querySelector(
        ".skio-bundle-builder__sticky-bar .skio-bundle-builder__progress-bar-text",
      ),
      checkoutButton: document.querySelector(".skio-bundle-builder__sticky-bar .skio-bundle-builder__sticky-checkout"),
      quantity: document.querySelector(".skio-bundle-builder__sticky-bar [data-remain-quantity]"),
      totalsWrapper: document.querySelector(".skio-bundle-builder__sticky-bar .skio-bundle-builder__totals-content"),
      subtotal: document.querySelector(".skio-bundle-builder__sticky-bar [data-subtotal-sum]"),
      compare: document.querySelector(".skio-bundle-builder__sticky-bar [data-subtotal-compare-sum]"),
      meal: document.querySelector(".skio-bundle-builder__sticky-bar [data-subtotal-meal]"),
    };

    this._bundleItems = [];
    this._upsellItems = [];
    this._sellingPlanId = null;
    this._selectableProductVariants = [];
    this._sellingPlanGroup = null;
    this._percentDiscountTier = null;
    this._totalQuantity = 0;
    this._totalSum = 0;
    this._removeAlert = null;
    this._boxId = null;
    // Intervals can point at another bundle product page (standard or sectioned). Those prices
    // come from that page's own Skio box. Map<"type:id", Promise<sections[]>>.
    this._linkedBundles = new Map();

    this._giftVariantIds = Array.from(
      document.querySelectorAll(".skio-bundle-builder__special-offer-item[data-gift-variant-id]"),
    )
      .map((el) => el.dataset.giftVariantId.trim())
      .filter(Boolean);

    this._listeners = [];
    this._preselectedSellingPlanFrequency = 8;
    this._mealQuantity = this._elements.container?.dataset.servesPerUnit;
    this._productType = this._elements.container?.dataset.productType;
    this._mealType = this._elements.container?.dataset.mealType;
    // Extras bundled with a subscription that inflate the compare-at price. Kept separate because a
    // double subscription buys twice the products but still only ships once.
    this._productsAddedCompare = parseFloat(this._elements.container?.dataset.productsAddedCompare || 0);
    this._freeShippingAddedCompare = parseFloat(this._elements.container?.dataset.freeShippingCompare || 0);

    // Double subscription: an extra purchase option that buys the same picks at the next size up
    // in the box's sizeInterval, on the same selling plan and at that size's percentDiscountTier.
    const sellingPlansEl = document.querySelector("[data-selling-plans]");
    this._enableDoubleSubscription = sellingPlansEl?.dataset.enableDoubleSubscription === "true";
    this._doubleSubscriptionBoxTitlePretext = sellingPlansEl?.dataset.doubleSubscriptionBoxTitlePretext || "";
    this._doubleSubscriptionText = sellingPlansEl?.dataset.doubleSubscriptionText || "";

    this._init();
  }

  async _init() {
    // Rules are storefront-wide and independent of this box, so they load alongside it.
    const [data] = await Promise.all([this._fetchData(), this._loadVolumeDiscountRules()]);
    this._addToCartText = this._elements.container?.dataset.addToCartText || "Add to Basket";
    this._oneTimeDiscountTiers = this._readOneTimeDiscountTiers();
    this._userCountryCode = this._elements.container?.dataset.userCountryCode;
    this._userCurrencyCode = this._elements.container?.dataset.userCurrencyCode;
    this._forceShakeProductIds = this._elements.container?.dataset.forceShakeProductIds?.split(",") || [];
    this._forceSoupsProductIds = this._elements.container?.dataset.forceSoupsProductIds?.split(",") || [];
    this._forceOatsProductIds = this._elements.container?.dataset.forceOatsProductIds?.split(",") || [];

    this._svgDelete = `
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.5 3.5L4.125 13.4999C4.15469 14.0778 4.575 14.5001 5.125 14.5001H10.875C11.4272 14.5001 11.8397 14.0778 11.875 13.4999L12.5001 3.5" stroke="black" stroke-width="0.9375" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M2.5 3.5H13.4999" stroke="black" stroke-width="0.9375" stroke-miterlimit="10" stroke-linecap="round"/>
      <path d="M10.25 5.5L10 12.5001M8 5.5V12.5001M5.75 5.5L6.00001 12.5001M6.00001 3.5V2.25001C5.99972 2.15144 6.01892 2.05377 6.05651 1.96265C6.0941 1.87152 6.14933 1.78873 6.21903 1.71903C6.28873 1.64933 6.37153 1.59409 6.46265 1.5565C6.55378 1.51892 6.65143 1.49972 6.75 1.5H9.25001C9.34858 1.49972 9.44623 1.51892 9.53736 1.5565C9.62848 1.59409 9.71128 1.64933 9.78098 1.71903C9.85068 1.78873 9.90591 1.87152 9.9435 1.96265C9.98109 2.05377 10.0003 2.15144 10 2.25001V3.5H6.00001Z" stroke="black" stroke-width="0.9375" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
    `;

    this._svgTick = `
      <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 6.5C12 8.0913 11.3679 9.61742 10.2426 10.7426C9.11742 11.8679 7.5913 12.5 6 12.5C4.4087 12.5 2.88258 11.8679 1.75736 10.7426C0.632141 9.61742 0 8.0913 0 6.5C0 4.9087 0.632141 3.38258 1.75736 2.25736C2.88258 1.13214 4.4087 0.5 6 0.5C7.5913 0.5 9.11742 1.13214 10.2426 2.25736C11.3679 3.38258 12 4.9087 12 6.5ZM9.0225 4.2275C8.96893 4.17411 8.90514 4.13208 8.83495 4.10391C8.76476 4.07574 8.68961 4.06202 8.61399 4.06356C8.53838 4.0651 8.46385 4.08187 8.39486 4.11288C8.32588 4.14388 8.26385 4.18848 8.2125 4.244L5.60775 7.56275L4.038 5.99225C3.93137 5.89289 3.79033 5.8388 3.64461 5.84137C3.49888 5.84394 3.35984 5.90297 3.25678 6.00603C3.15372 6.10909 3.09469 6.24813 3.09212 6.39386C3.08955 6.53958 3.14364 6.68062 3.243 6.78725L5.2275 8.7725C5.28096 8.82586 5.34462 8.86791 5.41469 8.89614C5.48475 8.92437 5.55979 8.93819 5.63531 8.93679C5.71083 8.93539 5.7853 8.9188 5.85427 8.88799C5.92324 8.85719 5.9853 8.81281 6.03675 8.7575L9.03075 5.015C9.13282 4.90887 9.18921 4.76696 9.18781 4.61972C9.1864 4.47248 9.12732 4.33166 9.02325 4.2275H9.0225Z" fill="#282828"/>
      </svg>
    `;

    this._svgDropdown = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10 13.3333L15.5556 7.7777" stroke="#121212" stroke-linecap="round"/>
        <path d="M10 13.3333L4.44444 7.7777" stroke="#121212" stroke-linecap="round"/>
      </svg>
    `;

    this._svgMinus = `
      <svg width="49" height="40" viewBox="0 0 49 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M31.0766 17.88V20.952H17.9246V17.88H31.0766Z" fill="#202020"/>
      </svg>
    `;

    this._svgPlus = `
      <svg width="53" height="40" viewBox="0 0 53 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M34.6621 20.984H28.1981V27.608H24.8061V20.984H18.3421V17.912H24.8061V11.288H28.1981V17.912H34.6621V20.984Z" fill="#202020"/>
      </svg>
    `;

    if (data && data.DynamicBox) {
      const dynamicBox = data.DynamicBox;
      this._sizeInterval = dynamicBox.sizeInterval;
      this._preselectedSize = dynamicBox.sizeInterval[0];
      this._selectableProductVariants = dynamicBox.selectableProductVariants || [];
      this._sellingPlanGroup = dynamicBox.sellingPlanGroup;
      this._percentDiscountTier = dynamicBox.percentDiscountTier;
      this._defaultOneTime = dynamicBox.settings.defaultOneTime;
      this._productGroupingOrder = dynamicBox.settings.productGroupingOrder;
      this._productGrouping = dynamicBox.settings.productGrouping;
      // New variant-mode settings (present when the Skio box is configured to select by variant)
      this._productVariantGrouping = dynamicBox.settings.productVariantGrouping;
      this._productVariantOrder = dynamicBox.settings.productVariantOrder;
      this._displayByVariant = Array.isArray(this._productVariantGrouping) && this._productVariantGrouping.length > 0;
      this._boxId = dynamicBox.boxId;
      this._oneTimeUpsells = dynamicBox.oneTimeUpsells;
      this._boxCurrencyCode = dynamicBox.currencyCode;

      if (this._userCurrencyCode !== this._boxCurrencyCode) {
        await this._overrideWithMarketPrices(this._userCountryCode);
      }

      this._groupedProducts = {};

      if (this._displayByVariant) {
        // Variant mode: render every product variant as its own card.
        this._buildVariantModeGrouping();
      } else {
        if (this._productGrouping?.Shakes) {
          this._productGrouping.Shakes = [...this._productGrouping.Shakes, ...this._forceShakeProductIds];
        }
        if (this._productGrouping?.Soups) {
          this._productGrouping.Soups = [...this._productGrouping.Soups, ...this._forceSoupsProductIds];
        }
        if (this._productGrouping?.Oats) {
          this._productGrouping.Oats = [...this._productGrouping.Oats, ...this._forceOatsProductIds];
        }

        this._selectableProductVariants.forEach((variant) => {
          const productId = variant.productPlatformId?.match(/\d+$/)?.[0];

          const categories = Object.keys(this._productGrouping || {}).filter((cat) =>
            this._productGrouping[cat].includes(productId),
          );

          if (categories.length === 0) categories.push("Other");

          categories.forEach((category) => {
            if (!this._groupedProducts[category]) {
              this._groupedProducts[category] = [];
            }

            const variantCopy = { ...variant };
            this._groupedProducts[category].push(variantCopy);
          });
        });
      }

      this._renderIntervalBlocks();
      this._renderSellingPlans(this._sellingPlanGroup);
      this._addSellingPlanPercent();
      this._setupPurchaseOptionListener();
      this._setupIntervalOptionListener();
      const intervalBlock = document.querySelector("[data-box-interval]");
      this._updateIntervalPriceBlock(intervalBlock);
      const size = this._getCheckedRadioValue("bundle-size");
      this._renderProductCards();
      this._renderProductUpsells();
      this._setupCheckoutButtonListener();
      this._updateRemainingQuantityBlock();
      this._updateMainPrice();
      this._renderProgressBarBlocks();
      this._renderStickyProgressBarBlocks();
      this._updateStickyUI();
      this._updatePaymentBlock();
      this._bindStickyCheckoutButton();
      this._updateMediaBySize(false);
      this._initStickyBarVisibilityObserver();
      this._updateDiscountBlockTop();
      this._initReviewsSlider();
      this._initCartGiftWatcher();
    }
  }

  async _fetchData() {
    try {
      const res = await fetch(this._apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
        },
        body: JSON.stringify({
          productPlatformId: this._productPlatformId,
        }),
      });

      if (!res.ok) {
        return null;
      }

      return await res.json();
    } catch (error) {
      console.error("Error:", error);
      return null;
    }
  }

  // Skio's storefront-wide quantity ("volume") discount rules, keyed by variant. This is a separate
  // Skio feature from a box's percentDiscountTier: a box whose products carry volume rules typically
  // has its tier zeroed and takes its whole discount from here. Rules cover every box on the
  // storefront, so linked bundles are priced from the same set.
  async _loadVolumeDiscountRules() {
    this._volumeDiscountRules = [];

    const shop = window.Shopify?.shop;
    if (!shop) return;

    try {
      const res = await fetch(
        `https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname?domain=${window.Shopify.shop}`,
        {
          headers: {
            Accept: "*/*",
          },
        },
      );

      if (!res.ok) return;

      const data = await res.json();

      // The box prices in percentages, so only percentage rules can be represented. Anything else is
      // dropped rather than silently mispriced.
      this._volumeDiscountRules = (data?.rules || [])
        .filter((rule) => {
          if (!Array.isArray(rule?.productVariantIds) || !rule.productVariantIds.length) return false;
          if (rule.discountType !== "PERCENTAGE") {
            console.warn("Skio volume discount rule ignored, unsupported discountType:", rule.discountType);
            return false;
          }
          return true;
        })
        .map((rule) => ({ ...rule, variantGids: new Set(rule.productVariantIds) }));
    } catch (error) {
      console.error("Error loading Skio volume discount rules:", error);
    }
  }

  // The deepest rule whose quantity range contains `quantity` and which covers every variant in
  // `variantGids`. Coverage has to be total: a rule covering only some of a box's variants could not
  // be applied uniformly across that box.
  _resolveVolumeDiscountPercent(variantGids, quantity) {
    if (!this._volumeDiscountRules?.length || !variantGids?.size || !(quantity > 0)) return 0;

    let percent = 0;

    this._volumeDiscountRules.forEach((rule) => {
      const min = rule.minQuantityToDiscount;
      const max = rule.maxQuantityToDiscount;

      // A null bound is open-ended on that side.
      if (min != null && quantity < min) return;
      if (max != null && quantity > max) return;
      if (![...variantGids].every((gid) => rule.variantGids.has(gid))) return;

      // Overlapping tiers are normal (1+/2+/3+/4+); the deepest matching one wins.
      percent = Math.max(percent, parseFloat(rule.discountAmount) || 0);
    });

    return percent;
  }

  // One-time quantity discounts come from the product metafield custom.bundle_one_time_discounts,
  // a list of metaobjects with item_quantity / discount_percentage. Metafields are Liquid-only, so
  // the section renders them into the page as JSON for us to read here.
  _readOneTimeDiscountTiers() {
    const script = document.querySelector("[data-one-time-discounts]");
    if (!script) return [];

    try {
      const parsed = JSON.parse(script.textContent);
      if (!Array.isArray(parsed)) return [];

      return (
        parsed
          .map((tier) => ({
            quantity: parseInt(tier?.item_quantity, 10),
            percent: parseFloat(tier?.discount_percentage),
          }))
          .filter((tier) => Number.isFinite(tier.quantity) && tier.quantity > 0 && Number.isFinite(tier.percent))
          // Ascending, so the lookup below can walk up to the deepest breakpoint the box reaches.
          .sort((a, b) => a.quantity - b.quantity)
      );
    } catch (error) {
      console.error("Error parsing one-time discount tiers JSON:", error);
      return [];
    }
  }

  // item_quantity is an "at least" breakpoint, so a box takes the highest tier it reaches rather
  // than needing an exact match: with tiers at 4 and 8 configured, a 12 pack falls back to the 8.
  _getOneTimeQuantityDiscountPercent(size) {
    if (!this._oneTimeDiscountTiers?.length || !(size > 0)) return 0;

    let percent = 0;
    for (const tier of this._oneTimeDiscountTiers) {
      if (tier.quantity > size) break;
      percent = tier.percent;
    }

    return percent;
  }

  // Volume discounts land on the whole box at once: the quantity tested is the box size, and the
  // winning rule's percentage comes off every product in the box.
  _getVolumeDiscountPercent(size) {
    if (!this._volumeDiscountRules?.length || !(size > 0)) return 0;

    this._volumeDiscountCache ||= {};
    if (this._volumeDiscountCache[size] != null) return this._volumeDiscountCache[size];

    if (!this._volumeDiscountGids) {
      this._volumeDiscountGids = new Set(
        this._getAllVariants(this._selectableProductVariants)
          .map((variant) => variant.platformId)
          .filter(Boolean),
      );
    }

    const percent = this._resolveVolumeDiscountPercent(this._volumeDiscountGids, size);
    this._volumeDiscountCache[size] = percent;

    return percent;
  }

  // Every price path reads the box discount here. The percentDiscountTier applies to every purchase
  // type; volume discounts are subscription-only, so they are added only when pricing a
  // subscription. Defaults to one-time, the safe direction for any caller that does not opt in.
  _getDiscountPercent(size, isSubscription = false) {
    const tierPercent = parseFloat(this._percentDiscountTier?.[size]) || 0;

    if (!isSubscription) {
      // The metafield ladder is the source of one-time quantity discounts when it is configured.
      // Without it, one-time pricing keeps falling back to the Skio tier as before.
      if (!this._oneTimeDiscountTiers?.length) return tierPercent;

      return Math.min(100, this._getOneTimeQuantityDiscountPercent(size));
    }

    // Tier and volume discounts are configured independently in Skio and sum, matching how the
    // selling-plan adjustment and bundle discount are already added together for the badge.
    return Math.min(100, tierPercent + this._getVolumeDiscountPercent(size));
  }

  // Linked bundles are other boxes on the same storefront, so the same rule set applies against
  // their own variants.
  _getLinkedSectionDiscountPercent(section, size, isSubscription = false) {
    const tierPercent = parseFloat(section?.percentDiscountTier?.[size]) || 0;
    if (!isSubscription || !this._volumeDiscountRules?.length || !section) return tierPercent;

    const cache = (section._volumeDiscountCache ||= {});

    if (cache[size] == null) {
      if (!section._volumeDiscountGids) {
        section._volumeDiscountGids = new Set(
          (section.variants || []).map((variant) => variant.platformId).filter(Boolean),
        );
      }
      cache[size] = this._resolveVolumeDiscountPercent(section._volumeDiscountGids, size);
    }

    return Math.min(100, tierPercent + cache[size]);
  }

  // --- Linked intervals -------------------------------------------------------------------
  // An interval can link to a different bundle product page instead of selecting a size here.
  // Its prices come from that bundle's own Skio box so they stay in sync with that page, and
  // they flip with the subscription/one-time toggle like every other interval.

  async _fetchLinkedBundleData(url, body) {
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        return null;
      }

      return await res.json();
    } catch (error) {
      console.error("Error:", error);
      return null;
    }
  }

  // Normalises both box shapes to the required sections we need for pricing. A standard bundle is
  // one section; a sectioned bundle sums its non-optional sections.
  _loadLinkedBundle(bundleId, bundleType) {
    if (!bundleId) return Promise.resolve(null);

    const cacheKey = `${bundleType}:${bundleId}`;

    if (!this._linkedBundles.has(cacheKey)) {
      const request =
        bundleType === "sectioned"
          ? this._fetchLinkedBundleData("https://api.skio.com/storefront-v2-http/get-sectioned-box", {
              sectionedBoxId: bundleId,
            }).then((data) =>
              data && data.ok && data.sectionedBox
                ? { dynamicBoxes: data.sectionedBox.dynamicBoxes || [], currencyCode: data.sectionedBox.currencyCode }
                : null,
            )
          : this._fetchLinkedBundleData("https://api.skio.com/storefront-http/get-dynamic-box", {
              productPlatformId: bundleId,
            }).then((data) =>
              data && data.DynamicBox
                ? { dynamicBoxes: [data.DynamicBox], currencyCode: data.DynamicBox.currencyCode }
                : null,
            );

      const promise = request
        .then(async (box) => {
          if (!box) return null;

          const sections = box.dynamicBoxes
            .filter((dynamicBox) => !dynamicBox.optional)
            .map((dynamicBox) => ({
              sizeInterval: dynamicBox.sizeInterval || [],
              percentDiscountTier: dynamicBox.percentDiscountTier || {},
              sellingPlanGroup: dynamicBox.sellingPlanGroup || {},
              variants: this._getAllVariants(dynamicBox.selectableProductVariants || []),
            }))
            .filter((section) => section.variants.length);

          if (!sections.length) return null;

          // Skio prices the linked box in its own currency, so it needs the same market override as
          // this box before anything prices it.
          if (this._userCurrencyCode !== box.currencyCode) {
            await this._applyMarketPrices(
              sections.flatMap((section) => section.variants),
              sections.map((section) => section.sellingPlanGroup),
              this._userCountryCode,
            );
          }

          return sections;
        })
        .catch((error) => {
          console.error("Error loading linked bundle", cacheKey, error);
          return null;
        });

      this._linkedBundles.set(cacheKey, promise);
    }

    return this._linkedBundles.get(cacheKey);
  }

  _getLinkedSectionCheapestAdjusted(section) {
    const plans = Object.values(section.sellingPlanGroup || {});
    if (!plans.length) return null;

    const selectableGids = new Set(section.variants.map((v) => v.platformId).filter(Boolean));

    let cheapest = null;

    for (const plan of plans) {
      const variantMapping = plan.variantMapping || {};

      for (const variantId in variantMapping) {
        if (!selectableGids.has(variantId)) continue;

        const variant = variantMapping[variantId];
        if (!cheapest || variant.adjustedPrice < cheapest.adjustedPrice) {
          cheapest = { adjustedPrice: this._roundedNumber(variant.adjustedPrice) };
        }
      }
    }

    return cheapest;
  }

  // Same arithmetic as this builder's own pricing, summed over the linked bundle's sections, so a
  // linked card shows exactly what that page shows.
  _getLinkedBundlePricing(sections, size, isSubscription, mealQuantity) {
    let finalPrice = 0;
    let comparePrice = 0;
    let oneTimeBase = 0;
    let savings = 0;

    sections.forEach((section) => {
      const cheapest = this._getCheapestVariant(section.variants);
      if (!cheapest) return;

      const percent = this._getLinkedSectionDiscountPercent(section, size, isSubscription);

      let unitPrice;
      if (isSubscription) {
        const adjusted = this._getLinkedSectionCheapestAdjusted(section);
        unitPrice = this._roundMoney(adjusted?.adjustedPrice > 0 ? adjusted.adjustedPrice : cheapest.price);
      } else {
        unitPrice = this._roundMoney(cheapest.price);
      }

      const unitDiscount = this._floorMoney(unitPrice * (percent / 100));
      finalPrice += (unitPrice - unitDiscount) * size;
      savings += unitDiscount * size;
      oneTimeBase += cheapest.price * size;

      const compareUnit =
        cheapest.compareAtPrice != null && cheapest.compareAtPrice > cheapest.price
          ? cheapest.compareAtPrice
          : cheapest.price;
      comparePrice += compareUnit * size;
    });

    // The free-gift value only applies to subscriptions, matching _calculateSubscriptionDiscountSum.
    const giftCompareValue = isSubscription ? this._getSubscriptionAddedCompare() : 0;
    comparePrice += giftCompareValue;

    const totalUnits = size * sections.length;
    const perMeal = totalUnits > 0 && mealQuantity > 0 ? finalPrice / (totalUnits * mealQuantity) : 0;

    return {
      finalPrice,
      comparePrice,
      perMeal,
      savings: isSubscription ? Math.max(0, oneTimeBase + giftCompareValue - finalPrice) : savings,
    };
  }

  // Pricing inputs for a linked interval card, or null until its bundle data has resolved.
  _getLinkedContext(container) {
    if (!container?.dataset.linkedBundleId) return null;

    const sections = container._linkedSections;
    if (!sections) return null;

    const configuredSize = parseFloat(container.dataset.linkedSize);
    const size =
      Number.isFinite(configuredSize) && configuredSize > 0 ? configuredSize : (sections[0].sizeInterval || [])[0];
    if (!size) return null;

    const servesPerUnit = parseFloat(container.dataset.linkedServesPerUnit);
    const mealQuantity =
      Number.isFinite(servesPerUnit) && servesPerUnit > 0 ? servesPerUnit : Number(this._mealQuantity);

    return { sections, size, mealQuantity };
  }

  // Fetches each linked interval's bundle and re-prices that card when it arrives.
  _loadLinkedIntervalPricing(container) {
    container.querySelectorAll("[data-linked-bundle-id]").forEach((linkedContainer) => {
      const { linkedBundleId, linkedBundleType } = linkedContainer.dataset;

      this._loadLinkedBundle(linkedBundleId, linkedBundleType).then((sections) => {
        if (!sections || !linkedContainer.isConnected) return;
        linkedContainer._linkedSections = sections;
        this._updateLinkedIntervalCard(linkedContainer, this._isSellingPlanSelected());
      });
    });
  }

  _updateLinkedIntervalCard(label, isSubscription) {
    const ctx = this._getLinkedContext(label);
    if (!ctx) return;

    const pricing = this._getLinkedBundlePricing(ctx.sections, ctx.size, isSubscription, ctx.mealQuantity);

    const priceDefault = label.querySelector(".price-default");
    if (priceDefault) priceDefault.textContent = this._formatMoney(pricing.finalPrice);

    const priceCompare = label.querySelector(".price-compare");
    if (priceCompare) {
      priceCompare.textContent =
        pricing.comparePrice > pricing.finalPrice ? this._formatMoney(pricing.comparePrice) : "";
    }

    const intervalBadge = label.querySelector(".skio-bundle-builder__interval-badge");
    if (intervalBadge) {
      const badgeText = label.closest("[data-box-interval]")?.getAttribute("data-badge-text") || "";
      intervalBadge.textContent = `${this._formatMoney(pricing.perMeal)} ${badgeText}`;
    }

    const intervalDiscountBlock = label.querySelector(".discount-block");
    if (intervalDiscountBlock) {
      intervalDiscountBlock.innerHTML =
        pricing.savings > 0 ? `<span>Save</span> ${this._formatMoney(pricing.savings)}` : "";
    }
  }

  _updateLinkedIntervalCards(block) {
    if (!block) return;
    const isSubscription = this._isSellingPlanSelected();

    block.querySelectorAll("[data-linked-bundle-id]").forEach((label) => {
      this._updateLinkedIntervalCard(label, isSubscription);
    });
  }

  // --- end linked intervals ---------------------------------------------------------------

  _roundMoney(value) {
    return Math.round(value * 100) / 100;
  }

  _floorMoney(value) {
    return Math.floor(value * 100) / 100;
  }

  // Formats in the shopper's currency with its own symbol ($, £, €), not a hardcoded "$".
  _formatMoney(amount) {
    const currency = this._userCurrencyCode || this._boxCurrencyCode || "AUD";
    let formatted;
    try {
      formatted = new Intl.NumberFormat("en", {
        style: "currency",
        currency,
        currencyDisplay: "narrowSymbol",
      }).format(Number(amount));
    } catch (error) {
      formatted = "$" + Number(amount).toFixed(2);
    }
    if (window.showCurrencyCode && this._userCurrencyCode) {
      return `${formatted} ${this._userCurrencyCode}`;
    }
    return formatted;
  }

  _getDiscountMultiplier(size, isSubscription = false) {
    const percent = this._getDiscountPercent(size, isSubscription);
    return (100 - percent) / 100;
  }

  _getCheapestSelectableVariant() {
    const allVariants = this._getAllVariants(this._selectableProductVariants);
    const cheapestVariant = this._getCheapestVariant(allVariants);
    return cheapestVariant;
  }

  _renderIntervalBlocks() {
    this._scaleElements = [];
    const container = document.querySelector("[data-box-interval]");
    if (!container) return;
    container.innerHTML = "";

    const productType = this._productType;
    const mealType = this._mealType;
    const intervals = JSON.parse(container.dataset.intervals);

    // Cards render in theme-editor block order. Intervals of this bundle still have to match one
    // of the box's sizes to appear (first block wins per size); linked intervals aren't sizes of
    // this box, so they're always kept.
    const seenSizes = new Set();
    const renderList = intervals
      .map((intervalOpt) => ({ size: parseFloat(intervalOpt.size) || 0, intervalOpt }))
      .filter(({ size, intervalOpt }) => {
        if (intervalOpt.linked_bundle_id) return true;
        if (seenSizes.has(size) || !this._sizeInterval.some((boxSize) => boxSize == size)) return false;
        seenSizes.add(size);
        return true;
      });

    renderList.forEach(({ size, intervalOpt }) => {
      const isLinked = !!intervalOpt.linked_bundle_id;

      // A linked interval navigates to another product page, so it's an anchor and must stay out
      // of the "bundle-size" radio group that drives this box's selected size.
      const label =
        isLinked && intervalOpt.product_link ? document.createElement("a") : document.createElement("label");

      label.classList.add("skio-bundle-builder__interval-container");

      let input = null;
      if (isLinked) {
        if (intervalOpt.product_link) label.href = intervalOpt.product_link;
        label.dataset.linkedBundleId = intervalOpt.linked_bundle_id;
        label.dataset.linkedBundleType = intervalOpt.linked_bundle_type || "standard";
        if (intervalOpt.linked_size) label.dataset.linkedSize = intervalOpt.linked_size;
        if (intervalOpt.linked_serves_per_unit) {
          label.dataset.linkedServesPerUnit = intervalOpt.linked_serves_per_unit;
        }
      } else {
        input = document.createElement("input");
        input.type = "radio";
        input.name = "bundle-size";
        input.value = size;
        input.classList.add("visually-hidden");
        if (input.value == this._preselectedSize) input.checked = true;

        label.appendChild(input);
      }

      const topBoxValue = intervalOpt.interval_top_badge;

      const topBadgeText = document.createElement("span");
      topBadgeText.classList.add("box-badge", "box-badge--orange", "top-badge");
      topBadgeText.textContent = topBoxValue;

      const sizeText = document.createElement("span");
      sizeText.classList.add("interval-title");
      sizeText.textContent = intervalOpt.title || `${size} ${productType}`;

      const mealText = document.createElement("span");
      mealText.classList.add("interval-text");
      if (intervalOpt.description) {
        mealText.innerHTML = intervalOpt.description;
      } else {
        mealText.textContent = `${size * this._mealQuantity} ${mealType}`;
      }

      const img = document.createElement("img");
      img.src = intervalOpt.image;
      img.alt = sizeText.textContent;

      const intervalPriceBlock = document.createElement("span");
      intervalPriceBlock.classList.add("skio-bundle-builder__price-block");

      const badgeText = container.getAttribute("data-badge-text");
      const intervalPriceBadge = document.createElement("span");
      intervalPriceBadge.classList.add("skio-bundle-builder__interval-badge", "box-badge", "box-badge--dark");

      if (isLinked) {
        // Placeholder from the linked product's Liquid price until its box data resolves.
        this._renderPlaceholderPriceContainer(intervalPriceBlock, intervalOpt);
      } else {
        const cheapestVariant = this._getCheapestSelectableVariant();
        this._renderPriceContainer(cheapestVariant, size, intervalPriceBlock);

        const mealPrice = cheapestVariant.price / this._mealQuantity;
        intervalPriceBadge.textContent = `${this._formatMoney(mealPrice)} ${badgeText}`;
      }

      if (topBoxValue) {
        label.appendChild(topBadgeText);
      }

      label.appendChild(img);
      label.appendChild(sizeText);
      label.appendChild(mealText);
      label.appendChild(intervalPriceBlock);

      if (isLinked) {
        const discountContainer = document.createElement("div");
        discountContainer.classList.add("discount-container");

        // Filled in by _updateLinkedIntervalCard once the linked bundle's pricing is available.
        const discountBlock = document.createElement("div");
        discountBlock.classList.add("discount-block", "box-badge");

        discountContainer.appendChild(discountBlock);
        label.appendChild(discountContainer);
      } else if (intervalOpt.savings_percentage) {
        const discountContainer = document.createElement("div");
        discountContainer.classList.add("discount-container");

        const discountBlock = document.createElement("div");
        discountBlock.classList.add("discount-block", "box-badge");
        const cheapestVariant = this._getCheapestSelectableVariant();
        const cheapestAdjustedVariant = this._getCheapestAdjustedVariant(this._sellingPlanGroup);
        const { difference } = this._calculateSubscriptionDiscountSum(cheapestVariant, cheapestAdjustedVariant, size);
        discountBlock.textContent = difference > 0 ? `Save ${this._formatMoney(difference)}` : "";

        discountContainer.appendChild(discountBlock);
        label.appendChild(discountContainer);
      }

      container.appendChild(label);
      if (input) this._scaleElements.push(input);
    });

    this._loadLinkedIntervalPricing(container);
  }

  _renderPlaceholderPriceContainer(priceBlock, intervalOpt) {
    priceBlock.innerHTML = "";

    const price = document.createElement("span");
    price.classList.add("price-default");
    price.textContent = intervalOpt.product_price || "";

    const comparePrice = document.createElement("span");
    comparePrice.classList.add("price-compare");
    comparePrice.textContent = intervalOpt.product_compare_at_price || "";

    priceBlock.appendChild(price);
    priceBlock.appendChild(comparePrice);
  }

  async _getMarketPrice(variantId, countryCode, sellingPlanGroup, interval) {
    const sellingPlanGroups = Object.values(sellingPlanGroup);

    const sellingPlanIntervalPercent =
      sellingPlanGroups.find((plan) => plan.intervalCount === interval)?.priceAdjustmentAmount || 0;

    const marketPrice = await this._fetchVariantMarketPrice(variantId, countryCode);
    const marketPriceAdjusted = (marketPrice * (100 - sellingPlanIntervalPercent)) / 100;
    return { marketPrice, marketPriceAdjusted };
  }

  _updateMainPrice() {
    const block = document.querySelector(".skio-bundle-builder__main-price");
    if (!block) return;

    const regularEl = block.querySelector(".price-regular");
    const compareEl = block.querySelector(".price-compare");
    const savingsEl = block.querySelector("[data-main-savings]");
    const savingsBlock = block.querySelector(".price-savings");

    const checkedInput = document.querySelector('input[name="bundle-size"]:checked');
    const size = checkedInput ? parseInt(checkedInput.value) : this._preselectedSize;

    const cheapestVariant = this._getCheapestSelectableVariant();
    const cheapestAdjusted = this._getCheapestAdjustedVariant(this._sellingPlanGroup);

    const selectedRadio = document.querySelector('input[name="purchase-option"]:checked');
    const isSubscription = selectedRadio?.dataset.radioType === "selling_plan";

    const percent = this._getDiscountPercent(size, isSubscription);

    let pricePerUnitFinal;
    let comparePrice;

    if (isSubscription) {
      const adjustedPrice =
        cheapestAdjusted?.adjustedPrice > 0 ? cheapestAdjusted.adjustedPrice : cheapestVariant.price;

      const rawUnitDiscount = this._roundMoney(adjustedPrice) * (percent / 100);
      const unitDiscount = this._floorMoney(rawUnitDiscount);
      pricePerUnitFinal = this._roundMoney(adjustedPrice) - unitDiscount;
      comparePrice = cheapestVariant.price;
    } else {
      const rawUnitDiscount = this._roundMoney(cheapestVariant.price) * (percent / 100);
      const unitDiscount = this._floorMoney(rawUnitDiscount);
      pricePerUnitFinal = cheapestVariant.price - unitDiscount;
      comparePrice = cheapestVariant.price;
    }

    const finalPrice = pricePerUnitFinal * size;
    const compareTotal = comparePrice * size + (isSubscription ? this._getSubscriptionAddedCompare() : 0);
    const savings = Math.max(0, compareTotal - finalPrice);

    if (regularEl) regularEl.textContent = this._formatMoney(finalPrice);
    if (compareEl) compareEl.textContent = savings > 0 ? this._formatMoney(compareTotal) : "";
    if (savingsEl) savingsEl.textContent = savings > 0 ? `${this._formatMoney(savings)} Off` : "";

    if (savingsBlock) {
      savings > 0 ? savingsBlock.classList.remove("custom-hidden") : savingsBlock.classList.add("custom-hidden");
    }
  }

  _fetchVariantMarketPrice(variantId, countryCode) {
    const query = `
      query productVariant($id: ID!) @inContext(country: ${countryCode}) {
        node(id: $id) {
          ... on ProductVariant {
            id
            price {
              amount
            }
            compareAtPrice{
              amount
            }
          }
        }
      }
    `;

    const response = fetch("/api/2026-01/graphql.json", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": "ae3098bf902396fd7f396049b17d60fb",
      },
      body: JSON.stringify({
        query,
        variables: { id: variantId },
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        return data.data.node.price.amount;
      })
      .catch((error) => {
        console.error("Error fetching variant market price:", error);
        return 0;
      });
    return response;
  }

  async _fetchBatchMarketPrices(variantIds, countryCode) {
    const ids = variantIds.map((id) => `"${id}"`).join(", ");
    const query = `
      query batchVariantPrices @inContext(country: ${countryCode}) {
        nodes(ids: [${ids}]) {
          ... on ProductVariant {
            id
            price { amount }
            compareAtPrice { amount }
          }
        }
      }
    `;

    try {
      // Tokenless Storefront API: variant prices need no access token, and a token that is revoked
      // fails the whole request with 401 rather than falling back. Prices stay in the base currency
      // whenever this fails, so the error is logged loudly.
      const res = await fetch("/api/2026-01/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query }),
      });
      const data = await res.json();
      if (!res.ok || data?.errors || !data?.data?.nodes) {
        console.error("Market price query failed, prices left in base currency:", res.status, data?.errors);
        return {};
      }
      const priceMap = {};
      for (const node of data.data.nodes) {
        if (!node?.id) continue;
        priceMap[node.id] = {
          price: parseFloat(node.price.amount),
          compareAtPrice: node.compareAtPrice ? parseFloat(node.compareAtPrice.amount) : null,
        };
      }
      return priceMap;
    } catch (error) {
      console.error("Error fetching batch market prices:", error);
      return {};
    }
  }

  async _overrideWithMarketPrices(countryCode) {
    const allVariants = this._getAllVariants(this._selectableProductVariants);
    const upsellVariants = this._getAllVariants(this._oneTimeUpsells || []);
    await this._applyMarketPrices([...allVariants, ...upsellVariants], [this._sellingPlanGroup], countryCode);
  }

  // Skio prices a box in the shop's base currency. Swap every variant (and every selling plan's
  // adjusted price) for the shopper's market price so all downstream math runs in their currency.
  async _applyMarketPrices(variants, sellingPlanGroups, countryCode) {
    if (!variants.length) return;

    const variantIds = [...new Set(variants.map((v) => v.platformId).filter(Boolean))];
    if (!variantIds.length) return;

    const priceMap = await this._fetchBatchMarketPrices(variantIds, countryCode);
    if (!Object.keys(priceMap).length) return;

    for (const variant of variants) {
      const marketData = priceMap[variant.platformId];
      if (!marketData) continue;
      variant.price = marketData.price;
      // A market without a compare-at price must clear it, not keep the base-currency one.
      variant.compareAtPrice = marketData.compareAtPrice;
    }

    sellingPlanGroups.forEach((sellingPlanGroup) => {
      for (const plan of Object.values(sellingPlanGroup || {})) {
        if (!plan.variantMapping) continue;
        const adjustmentPercent = plan.priceAdjustmentAmount || 0;
        for (const gid in plan.variantMapping) {
          const marketData = priceMap[gid];
          if (!marketData) continue;
          plan.variantMapping[gid].adjustedPrice = this._roundedNumber(
            (marketData.price * (100 - adjustmentPercent)) / 100,
          );
        }
      }
    });
  }

  _getCheapestVariant(allVariants) {
    if (!allVariants || allVariants.length === 0) return null;

    const cheapest = allVariants.reduce((cheapest, variant) => {
      return variant.price < cheapest.price ? variant : cheapest;
    }, allVariants[0]);

    return {
      price: cheapest.price,
      compareAtPrice: cheapest.compareAtPrice,
      platformId: cheapest.platformId,
      id: cheapest.id,
    };
  }

  _getAllVariants(products) {
    if (!Array.isArray(products)) return [];
    return products.flatMap((product) => product.productVariants || []);
  }

  _getCheapestAdjustedVariant(sellingPlans) {
    if (!sellingPlans || typeof sellingPlans !== "object") return null;
    const plans = Object.values(sellingPlans);
    if (!plans.length) return null;

    const selectableGids = new Set(
      this._getAllVariants(this._selectableProductVariants)
        .map((v) => v.platformId)
        .filter(Boolean),
    );

    let cheapestVariant = null;

    for (const planId in plans) {
      const variantMapping = plans[planId].variantMapping;

      for (const variantId in variantMapping) {
        if (!selectableGids.has(variantId)) continue;

        const variant = variantMapping[variantId];

        if (!cheapestVariant || variant.adjustedPrice < cheapestVariant.adjustedPrice) {
          cheapestVariant = {
            variantId,
            planId: variant.planId,
            adjustedPrice: this._roundedNumber(variant.adjustedPrice),
          };
        }
      }
    }

    return cheapestVariant;
  }

  _roundedNumber(value) {
    return Math.round(value * 100) / 100;
  }

  _getMaxPriceAdjustmentAmount(sellingPlans) {
    if (!sellingPlans || typeof sellingPlans !== "object") return 0;
    let maxAdjustment = 0;

    const plansArray = Object.values(sellingPlans);
    if (!plansArray.length) return 0;

    for (const plan of plansArray) {
      if (plan.priceAdjustmentAmount > maxAdjustment) {
        maxAdjustment = plan.priceAdjustmentAmount;
      }
    }

    return maxAdjustment;
  }

  _renderSellingPlans(sellingPlanGroup) {
    if (!this._hasSellingPlans()) {
      return;
    }

    const container = document.querySelector("[data-selling-plans]");
    if (!container) return;
    container.innerHTML = "";

    const cheapestVariant = this._getCheapestSelectableVariant();
    const cheapestAdjustedVariant = this._getCheapestAdjustedVariant(sellingPlanGroup);
    const size = this._getSelectedBundleTotalSize();

    const { sortedPlans, planIds } = this._getSortedPlans(sellingPlanGroup);

    const sellingPlansContainer = this._createSellingPlansSection(
      sortedPlans,
      planIds,
      cheapestVariant,
      cheapestAdjustedVariant,
      size,
    );
    container.appendChild(sellingPlansContainer);

    if (this._isDoubleSubscriptionEnabled()) {
      const doubleContainer = this._createDoubleSellingPlansSection(
        sortedPlans,
        planIds,
        cheapestVariant,
        cheapestAdjustedVariant,
        size,
        this._getDoubleSubscriptionSize(),
      );
      container.appendChild(doubleContainer);
    }

    const oneTimeContainer = this._createOneTimeSection(cheapestVariant, size);
    container.appendChild(oneTimeContainer);

    this._attachPurchaseOptionHandlers();
    this._updateSavingsPercentInBenefitsList(size);
  }

  _getSortedPlans(sellingPlanGroup) {
    const sortedPlans = Object.values(sellingPlanGroup).sort((a, b) => a.position - b.position);

    const planIdsOrdered = Object.keys(sellingPlanGroup).sort(
      (a, b) => sellingPlanGroup[a].position - sellingPlanGroup[b].position,
    );

    const planIds = planIdsOrdered.map((id) => id.split("/").pop());

    return { sortedPlans, planIds };
  }

  _createSellingPlansSection(plans, planIds, cheapestVariant, cheapestAdjustedVariant, size) {
    const container = document.createElement("div");
    container.classList.add(
      "skio-bundle-builder__purchase-option",
      "skio-bundle-builder__purchase-option--selling-plans",
    );

    const title = this._createSellingPlansTitle(window.subscriptionBoxTitlePretext);
    container.appendChild(title);

    const subtitleContainer = this._createSellingPlansSubtitle(cheapestVariant, cheapestAdjustedVariant, size);
    container.appendChild(subtitleContainer);

    const dropdown = this._createPlansDropdown(plans, planIds, size);
    container.appendChild(dropdown);

    if (window.listItems && Array.isArray(window.listItems)) {
      const list = this._createBenefitsList();
      container.appendChild(list);
    }

    return container;
  }

  _isDoubleSubscriptionEnabled() {
    return this._enableDoubleSubscription === true && this._hasSellingPlans() && !!this._getDoubleSubscriptionSize();
  }

  // `size` is what the customer picked; `doubleSize` is the larger sizeInterval this option buys.
  // Prices, per-meal figures and the savings badge are all just the normal maths at doubleSize.
  _createDoubleSellingPlansSection(plans, planIds, cheapestVariant, cheapestAdjustedVariant, size, doubleSize) {
    const container = document.createElement("div");
    container.classList.add(
      "skio-bundle-builder__purchase-option",
      "skio-bundle-builder__purchase-option--selling-plans",
      "skio-bundle-builder__purchase-option--double-selling-plans",
    );

    const title = this._createSellingPlansTitle(this._doubleSubscriptionBoxTitlePretext);
    container.appendChild(title);

    const subtitleContainer = this._createSellingPlansSubtitle(
      cheapestVariant,
      cheapestAdjustedVariant,
      doubleSize,
      true,
    );
    container.appendChild(subtitleContainer);

    // Frequency choices stay keyed to the picked size so both blocks offer the same plans; only
    // the discount label inside them reflects the larger tier.
    const dropdown = this._createPlansDropdown(plans, planIds, size, true, doubleSize);
    container.appendChild(dropdown);

    if (this._doubleSubscriptionText) {
      const text = this._createDoubleSubscriptionList(this._doubleSubscriptionText);
      if (text) container.appendChild(text);
    }

    return container;
  }

  // Each line (or semicolon-separated part, as the setting was originally written)
  // becomes a list item. Leading bullet characters are stripped so a merchant
  // pasting "- Free shipping" doesn't get a double bullet.
  _createDoubleSubscriptionList(rawText) {
    const items = rawText
      .split(/\r?\n|<br\s*\/?>|;/i)
      .map((line) => line.replace(/^\s*[-–—•*]\s*/, "").trim())
      .filter((line) => line.length > 0);

    if (items.length === 0) return null;

    const list = document.createElement("ul");
    list.classList.add("skio-bundle-builder__double-subscription-text");

    items.forEach((item) => {
      const li = document.createElement("li");
      li.textContent = item;
      list.appendChild(li);
    });

    return list;
  }

  _createSellingPlansTitle(pretext) {
    const title = document.createElement("div");
    title.classList.add("skio-bundle-builder__selling-plans-title");

    const content = document.createElement("div");
    content.classList.add("skio-bundle-builder__selling-plans-content");
    const pretextText = pretext || "Save";
    content.innerHTML = `${pretextText} <span data-total-percent></span>`;

    title.appendChild(content);
    return title;
  }

  _createSellingPlansSubtitle(cheapestVariant, cheapestAdjustedVariant, size, isDouble = false) {
    const container = document.createElement("div");
    container.classList.add("skio-bundle-builder__purchase-subtitle-container");

    const topRowContainer = document.createElement("div");
    topRowContainer.classList.add("skio-bundle-builder__purchase-subtitle-top-row");

    const subtitle = document.createElement("div");
    subtitle.classList.add("skio-bundle-builder__purchase-subtitle");
    subtitle.textContent = isDouble ? "Double Subscription" : window.subscriptionTextTitle;
    topRowContainer.appendChild(subtitle);

    container.appendChild(topRowContainer);

    const content = document.createElement("div");
    content.classList.add("skio-bundle-builder__purchase-content");
    container.appendChild(content);

    const priceBlock = document.createElement("span");
    priceBlock.classList.add(
      "skio-bundle-builder__price-block",
      isDouble ? "double-subscription-price" : "subscription-price",
    );

    this._renderPriceContainer(cheapestVariant, size, priceBlock, false, true, false, isDouble);

    const adjustedPrice =
      cheapestAdjustedVariant?.adjustedPrice > 0 ? cheapestAdjustedVariant.adjustedPrice : cheapestVariant.price;

    const discountMultiplier = this._getDiscountMultiplier(size, true);
    const finalPrice = adjustedPrice * discountMultiplier;

    const badgeBlock = document.createElement("span");
    badgeBlock.classList.add("skio-bundle-builder__badge-block");

    const mealPriceBlock = this._createMealPriceBlock(finalPrice, size);
    topRowContainer.appendChild(mealPriceBlock);

    /*  const saveBadge = this._createSaveBadge(cheapestVariant, cheapestAdjustedVariant, size);
    badgeBlock.appendChild(saveBadge); */

    content.appendChild(priceBlock);
    content.appendChild(badgeBlock);

    return container;
  }

  _getMealPrice(pricePerUnit, size) {
    const mealQuantity = this._mealQuantity;

    if (!pricePerUnit || !size || !mealQuantity) return null;

    const totalPrice = pricePerUnit * size;
    const totalMeals = size * mealQuantity;

    return this._formatMoney(totalPrice / totalMeals);
  }

  _mealTypeSingular() {
    return this._mealType.endsWith("s") ? this._mealType.slice(0, -1) : this._mealType;
  }

  _createMealPriceBlock(price, size) {
    const mealPrice = this._getMealPrice(price, size);
    if (!mealPrice) return null;

    const mealType = this._mealType.endsWith("s") ? this._mealType.slice(0, -1) : this._mealType;

    const costPerMeal = document.createElement("span");
    costPerMeal.classList.add("skio-bundle-builder__cost-per-meal");
    costPerMeal.textContent = `${mealPrice} `;

    const mealPriceSuffix = document.createElement("span");
    mealPriceSuffix.classList.add("skio-bundle-builder__meal-price-suffix");
    mealPriceSuffix.textContent = `per ${mealType.toLowerCase()}`;

    const mealPriceText = document.createElement("span");
    mealPriceText.classList.add("skio-bundle-builder__meal-price");
    mealPriceText.appendChild(costPerMeal);
    mealPriceText.appendChild(mealPriceSuffix);

    return mealPriceText;
  }

  _createSaveBadge(cheapestVariant, cheapestAdjustedVariant, size) {
    const badge = document.createElement("div");
    badge.classList.add("skio-bundle-builder__selling-plans-save", "box-badge");

    const { difference } = this._calculateSubscriptionDiscountSum(cheapestVariant, cheapestAdjustedVariant, size);
    badge.textContent = `${this._formatMoney(difference)} OFF`;

    return badge;
  }

  _createOneTimeSaveBadge(cheapestVariant, size) {
    const badge = document.createElement("div");
    badge.classList.add("skio-bundle-builder__one-time-save", "box-badge");

    const percent = this._getDiscountPercent(size);
    const unitDiscount = this._floorMoney(this._roundMoney(cheapestVariant.price) * (percent / 100));
    const discountAmount = unitDiscount * size;

    badge.textContent = `${this._formatMoney(discountAmount)} OFF`;

    return badge;
  }

  _filterPlansBySize(plans, planIds, size, isDouble = false) {
    let filteredPlans = [...plans];
    let filteredPlanIds = [...planIds];

    // A small bundle normally drops the longest frequency, but a double order is twice the
    // product, so the double option keeps it (and defaults to it).
    if (size == 4 && !isDouble) {
      filteredPlans.pop();
      filteredPlanIds.pop();
    }

    if (size == 8 || size == 12) {
      filteredPlans.shift();
      filteredPlanIds.shift();
    }

    return {
      plans: filteredPlans,
      planIds: filteredPlanIds,
    };
  }

  // `size` drives which frequencies are offered; `pricingSize` drives the discount label, which
  // for the double option is the larger sizeInterval it buys.
  _createPlansDropdown(plans, planIds, size, isDouble = false, pricingSize = size) {
    const dropdown = document.createElement("div");
    dropdown.classList.add("custom-dropdown");

    const bundleDiscount = this._getDiscountPercent(pricingSize, true);

    const icon = document.createElement("div");
    icon.classList.add("dropdown-icon");
    icon.innerHTML = this._svgDropdown;
    dropdown.appendChild(icon);

    const selected = document.createElement("div");
    selected.classList.add("selected");
    selected.textContent = "Select a plan";
    dropdown.appendChild(selected);

    const optionsContainer = document.createElement("div");
    optionsContainer.classList.add("options");
    const optionsList = document.createElement("div");
    optionsList.classList.add("options__list");
    optionsContainer.appendChild(optionsList);
    dropdown.appendChild(optionsContainer);

    const filtered = this._filterPlansBySize(plans, planIds, size, isDouble);

    this._populatePlansOptions(optionsList, filtered.plans, filtered.planIds, bundleDiscount, selected, isDouble);

    this._attachDropdownHandlers(selected, optionsContainer);

    return dropdown;
  }

  _findSellingPlanIndexByInterval(intervalCount) {
    const sellingPlansData = this._sellingPlanGroup;

    if (!sellingPlansData) return -1;

    const plan = Object.values(sellingPlansData).find((plan) => plan.intervalCount == intervalCount);

    return plan ? plan.position : -1;
  }

  // _populatePlansOptions(container, plans, planIds, bundleDiscount, selectedElement) {
  //   const preselectedIndex = this._findSellingPlanIndexByInterval(this._preselectedSellingPlanFrequency);

  //   plans.forEach((plan, idx) => {
  //     const label = document.createElement("label");
  //     const input = this._createPlanInput(plan, planIds[idx]);

  //     label.appendChild(input);

  //     const totalDiscount = plan.priceAdjustmentAmount + bundleDiscount;

  //     label.insertAdjacentHTML(
  //       "beforeend",
  //       `Delivery ${plan.title.toLowerCase()} <span class="visually-hidden"> -<span data-total-discount>${totalDiscount}</span>% OFF</span>`,
  //     );

  //     const size = parseInt(this._getSelectedBundleTotalSize());

  //     if (!this._defaultOneTime) {
  //       if (size == 4 && idx === 0) {
  //         input.checked = true;
  //         this._updateSelectedText(label, selectedElement);
  //       } else if (size != 4 && idx === preselectedIndex) {
  //         input.checked = true;
  //         this._updateSelectedText(label, selectedElement);
  //       }
  //     }

  //     container.appendChild(label);
  //   });
  // }

  _populatePlansOptions(container, plans, planIds, bundleDiscount, selectedElement, isDouble = false) {
    const size = parseInt(this._getSelectedBundleTotalSize());

    plans.forEach((plan, idx) => {
      const label = document.createElement("label");
      const input = this._createPlanInput(plan, planIds[idx], isDouble);
      label.appendChild(input);

      const totalDiscount = plan.priceAdjustmentAmount + bundleDiscount;
      label.insertAdjacentHTML(
        "beforeend",
        `Delivery ${plan.title.toLowerCase()} <span class="visually-hidden"> -<span data-total-discount>${totalDiscount}</span>% OFF</span>`,
      );

      // The normal subscription option stays the default selection; the double one starts empty.
      if (!this._defaultOneTime && !isDouble) {
        let shouldCheck = false;

        if (size == 4) {
          shouldCheck = plans.length > 1 ? idx === 1 : idx === 0;
        } else {
          shouldCheck = idx === plans.length - 1;
        }

        if (shouldCheck) {
          input.checked = true;
          this._updateSelectedText(label, selectedElement);
        }
      }

      container.appendChild(label);
    });
  }

  _createPlanInput(plan, planId, isDouble = false) {
    const input = document.createElement("input");
    input.classList.add("visually-hidden");
    input.type = "radio";
    input.name = "purchase-option";
    input.value = planId;
    // Double subscriptions keep the "selling_plan" type so every price/gift path treats them as a
    // subscription; the size they buy is what differs.
    input.dataset.radioType = "selling_plan";
    input.dataset.priceAdjustmentAmount = plan.priceAdjustmentAmount;
    if (isDouble) input.dataset.doubleSubscription = "true";

    return input;
  }

  _isDoubleSubscriptionSelected() {
    const selected = document.querySelector('input[name="purchase-option"]:checked');
    return selected?.dataset.doubleSubscription === "true";
  }

  // The double option is the next size up in the box's sizeInterval. Boxes that use this feature
  // are configured so that size is a whole multiple of the one below it, which is what makes
  // "the same picks, more of each" come out exact. Defaults to the picked size, but interval cards
  // pass their own size so each can preview what it would cost as a double.
  _getDoubleSubscriptionSize(fromSize) {
    const currentSize = parseInt(fromSize ?? this._getSelectedBundleTotalSize());
    if (!currentSize || !Array.isArray(this._sizeInterval)) return null;

    const nextSize = this._sizeInterval
      .map((value) => parseInt(value))
      .filter((value) => Number.isFinite(value) && value > currentSize)
      .sort((a, b) => a - b)[0];

    if (!nextSize || nextSize % currentSize !== 0) return null;

    return nextSize;
  }

  _getPurchaseQuantityMultiplier() {
    if (!this._isDoubleSubscriptionSelected()) return 1;

    const currentSize = parseInt(this._getSelectedBundleTotalSize());
    const doubleSize = this._getDoubleSubscriptionSize();
    if (!currentSize || !doubleSize) return 1;

    return doubleSize / currentSize;
  }

  // The size whose percentDiscountTier applies to what is actually being bought. Picking the
  // double option moves the whole box onto the larger size's tier.
  _getEffectivePricingSize() {
    const currentSize = parseInt(this._getSelectedBundleTotalSize());
    if (!this._isDoubleSubscriptionSelected()) return currentSize;

    return this._getDoubleSubscriptionSize() || currentSize;
  }

  // Value of the subscription extras to add on top of a compare-at price. Doubling the order doubles
  // the free products, but free shipping is only ever given once because it still ships once.
  // The values are theme settings entered in the shop's base currency, so they are converted to the
  // shopper's currency the same way Shopify converts prices.
  _getSubscriptionAddedCompare(quantityMultiplier = 1) {
    const baseValue = this._productsAddedCompare * quantityMultiplier + this._freeShippingAddedCompare;
    return this._roundedNumber(baseValue * this._getCurrencyRate());
  }

  // Shopify's base-to-presentment conversion rate; 1 when the shopper is in the base currency.
  _getCurrencyRate() {
    const rate = parseFloat(window.Shopify?.currency?.rate);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  _updateSelectedText(label, selectedElement) {
    const clone = label.cloneNode(true);
    const inputInClone = clone.querySelector("input");
    if (inputInClone) inputInClone.remove();
    selectedElement.innerHTML = clone.innerHTML.trim();
  }

  _attachDropdownHandlers(selected, optionsContainer) {
    const toggleOptions = () => {
      const isVisible = optionsContainer.style.display === "block";
      optionsContainer.style.display = isVisible ? "none" : "block";
    };

    selected.addEventListener("click", toggleOptions);
    this._listeners.push({ el: selected, type: "click", fn: toggleOptions });

    optionsContainer.querySelectorAll('input[type="radio"]').forEach((input) => {
      const onPlanSelect = () => {
        input.checked = true;
        const label = input.closest("label");
        if (!label) return;

        this._updateSelectedText(label, selected);
        optionsContainer.style.display = "none";

        // Picking a plan in one option block deselects every other block, so clear their labels.
        this._resetOtherPlanDropdowns(input.closest(".skio-bundle-builder__purchase-option"));

        this._updateIntervalPriceBlock(document.querySelector("[data-box-interval]"));
        this._updateMainPrice();
        this._updateDiscountBlockTop();
        this._updateBundleItemsForPurchaseOption();
      };

      input.addEventListener("change", onPlanSelect);
      this._listeners.push({ el: input, type: "change", fn: onPlanSelect });
    });
  }

  _resetOtherPlanDropdowns(activeBlock) {
    document.querySelectorAll(".skio-bundle-builder__purchase-option--selling-plans").forEach((block) => {
      if (block === activeBlock) return;

      block
        .querySelectorAll('input[type="radio"][data-radio-type="selling_plan"]')
        .forEach((radio) => (radio.checked = false));

      const selected = block.querySelector(".custom-dropdown .selected");
      if (selected) selected.textContent = "Select a plan";

      const optionsContainer = block.querySelector(".options");
      if (optionsContainer) optionsContainer.style.display = "none";
    });
  }

  _createBenefitsList() {
    const ul = document.createElement("ul");
    ul.classList.add("skio-bundle-builder__subscription-list");

    window.listItems.forEach((item, index) => {
      const li = document.createElement("li");
      li.innerHTML = `<span>${item.trim()}</span>`;
      if (index === 1) li.dataset.savingsPercent = "true";
      ul.appendChild(li);
    });

    return ul;
  }

  _updateSavingsPercentInBenefitsList(size) {
    const container = document.querySelector("[data-box-interval]");
    if (!container) return;
    const intervals = JSON.parse(container.dataset.intervals);
    const intervalOpt = intervals.find((opt) => opt.size == size && !opt.linked_bundle_id);
    if (!intervalOpt || !intervalOpt.savings_percentage) return;

    const firstLi = document.querySelector(".skio-bundle-builder__subscription-list [data-savings-percent]");
    if (!firstLi) return;

    const span = firstLi.querySelector("span");
    if (!span) return;

    const sizeBonusMap = { 4: 9, 8: 4, 12: 2 };
    const sizeBonus = sizeBonusMap[size] ?? 0;

    span.innerHTML = span.innerHTML.replace(/\d+%/, `${+intervalOpt.savings_percentage}%`);
  }

  _createOneTimeSection(cheapestVariant, size) {
    const container = document.createElement("div");
    container.classList.add("skio-bundle-builder__purchase-option", "skio-bundle-builder__purchase-option--one-time");

    const title = this._createSellingPlansTitle(window.oneTimeBoxTitlePretext);
    container.appendChild(title);

    const label = document.createElement("label");

    const input = this._createOneTimeInput();

    const hasSellingPlans = this._sellingPlanGroup && Object.keys(this._sellingPlanGroup).length > 0;
    if (!hasSellingPlans) {
      input.checked = true;
    }

    label.appendChild(input);

    const subtitleContainer = this._createOneTimeSubtitle(cheapestVariant, size);
    label.appendChild(subtitleContainer);

    if (window.oneTimePurchaseItem) {
      const text = document.createElement("div");
      text.classList.add("skio-bundle-builder__one-time-text");
      text.textContent = window.oneTimePurchaseItem;
      label.appendChild(text);
    }

    container.appendChild(label);
    return container;
  }

  _createOneTimeInput() {
    const input = document.createElement("input");
    input.classList.add("visually-hidden");
    input.type = "radio";
    input.name = "purchase-option";
    input.value = "one-time";
    input.dataset.radioType = "one_time_purchase";

    if (this._defaultOneTime) {
      input.checked = true;
    }

    return input;
  }

  _createOneTimeSubtitle(cheapestVariant, size) {
    const container = document.createElement("div");
    container.classList.add("skio-bundle-builder__purchase-subtitle-container");

    const topRowContainer = document.createElement("div");
    topRowContainer.classList.add("skio-bundle-builder__purchase-subtitle-top-row");

    const subtitle = document.createElement("div");
    subtitle.classList.add("skio-bundle-builder__purchase-subtitle");
    subtitle.textContent = "One Time Purchase";
    topRowContainer.appendChild(subtitle);
    container.appendChild(topRowContainer);

    const content = document.createElement("div");
    content.classList.add("skio-bundle-builder__purchase-content");
    container.appendChild(content);

    const percent = this._getDiscountPercent(size);
    const itemPrice = this._roundMoney(cheapestVariant.price);
    const unitDiscount = this._floorMoney(itemPrice * (percent / 100));
    const finalPrice = itemPrice - unitDiscount;

    const priceBlock = document.createElement("span");
    priceBlock.classList.add("skio-bundle-builder__price-block", "one-time-price");
    this._renderPriceContainer(cheapestVariant, size, priceBlock, false, false, true);

    const badgeBlock = document.createElement("span");
    badgeBlock.classList.add("skio-bundle-builder__badge-block");

    const mealPriceBlock = this._createMealPriceBlock(finalPrice, size);
    topRowContainer.appendChild(mealPriceBlock);

    /*  const saveBadge = this._createOneTimeSaveBadge(cheapestVariant, size);
    badgeBlock.appendChild(saveBadge); */

    content.appendChild(priceBlock);
    content.appendChild(badgeBlock);

    return container;
  }

  _attachPurchaseOptionHandlers() {
    const sellingPlansBlock = this._getSellingPlansBlock();
    const doubleSellingPlansBlock = this._getDoubleSellingPlansBlock();
    const oneTimeBlock = document.querySelector(".skio-bundle-builder__purchase-option--one-time");
    if (!sellingPlansBlock || !oneTimeBlock) return;
    const intervalBlock = document.querySelector("[data-box-interval]");

    const onSellingPlansClick = (e) => {
      if (e.target.closest(".custom-dropdown")) return;
      this._handleSellingPlansSelection(sellingPlansBlock, oneTimeBlock, intervalBlock);
      this._elements.paymentsBlock?.classList.add("hidden");
    };

    const onOneTimeClick = () => {
      this._handleOneTimeSelection(sellingPlansBlock, oneTimeBlock, intervalBlock);
      this._elements.paymentsBlock?.classList.remove("hidden");
    };

    this._addListener(sellingPlansBlock, "click", onSellingPlansClick);
    this._addListener(oneTimeBlock, "click", onOneTimeClick);

    if (doubleSellingPlansBlock) {
      const onDoubleSellingPlansClick = (e) => {
        if (e.target.closest(".custom-dropdown")) return;
        this._handleSellingPlansSelection(doubleSellingPlansBlock, oneTimeBlock, intervalBlock);
        this._elements.paymentsBlock?.classList.add("hidden");
      };

      this._addListener(doubleSellingPlansBlock, "click", onDoubleSellingPlansClick);
    }
  }

  _getSellingPlansBlock() {
    return document.querySelector(
      ".skio-bundle-builder__purchase-option--selling-plans:not(.skio-bundle-builder__purchase-option--double-selling-plans)",
    );
  }

  _getDoubleSellingPlansBlock() {
    return document.querySelector(".skio-bundle-builder__purchase-option--double-selling-plans");
  }

  _handleSellingPlansSelection(sellingPlansBlock, oneTimeBlock, intervalBlock) {
    const isAlreadySelected =
      sellingPlansBlock.classList.contains("active") ||
      [...sellingPlansBlock.querySelectorAll('input[type="radio"][data-radio-type="selling_plan"]')].some(
        (r) => r.checked,
      );

    if (isAlreadySelected) return;

    // const sellingPlanRadios = sellingPlansBlock.querySelectorAll('input[type="radio"][data-radio-type="selling_plan"]');

    // const size = parseInt(this._getSelectedBundleTotalSize());

    // let defaultRadio;
    // if (size === 4) {
    //   defaultRadio = sellingPlanRadios[0];
    // } else {
    //   const defaultPosition = this._findSellingPlanIndexByInterval(this._preselectedSellingPlanFrequency);
    //   defaultRadio = sellingPlanRadios[defaultPosition] || sellingPlanRadios[0];
    // }
    const sellingPlanRadios = sellingPlansBlock.querySelectorAll('input[type="radio"][data-radio-type="selling_plan"]');
    const size = parseInt(this._getSelectedBundleTotalSize());

    const isDouble = sellingPlansBlock.classList.contains("skio-bundle-builder__purchase-option--double-selling-plans");

    let defaultRadio;
    if (isDouble) {
      // The double plan always defaults to the longest frequency, whatever the bundle size.
      defaultRadio = sellingPlanRadios[1] || sellingPlanRadios[0];
    } else if (size === 4) {
      defaultRadio = sellingPlanRadios[1] || sellingPlanRadios[0];
    } else {
      defaultRadio = sellingPlanRadios[1] || sellingPlanRadios[0];
    }

    if (defaultRadio) {
      defaultRadio.checked = true;

      const selected = sellingPlansBlock.querySelector(".selected");
      this._updateSelectedLabel(defaultRadio, selected);
    }

    // Clear the sibling subscription block (normal vs double) so only one plan reads as chosen.
    this._resetOtherPlanDropdowns(sellingPlansBlock);

    // After the reset, so the interval cards price off the option that ended up selected.
    this._updateIntervalPriceBlock(intervalBlock);

    this._setRadioChecked(oneTimeBlock, false);
    this._updatePurchaseOption();
    this._updateMainPrice();
    this._updateDiscountBlockTop();
  }

  _handleOneTimeSelection(sellingPlansBlock, oneTimeBlock, intervalBlock) {
    this._setRadioChecked(oneTimeBlock, true);

    // Passing null clears every subscription block (normal and double).
    this._resetOtherPlanDropdowns(null);

    this._updateIntervalPriceBlock(intervalBlock);
    this._updatePurchaseOption();
    this._updateMainPrice();
    this._updateDiscountBlockTop();
  }

  _updatePurchaseOption() {
    this._updateBundleItemsForPurchaseOption();
  }

  _setRadioChecked(element, checked) {
    const radio = element?.querySelector('input[type="radio"]');
    if (radio) radio.checked = checked;
  }

  _updateSelectedLabel(firstRadio, selectedElement) {
    if (!firstRadio || !selectedElement) return;

    const label = firstRadio.closest("label");
    if (!label) return;

    const clone = label.cloneNode(true);
    clone.querySelector("input")?.remove();
    selectedElement.innerHTML = clone.innerHTML.trim();
  }

  _addListener(element, type, handler) {
    element.addEventListener(type, handler);
    this._listeners.push({ el: element, type, fn: handler });
  }

  _getSelectedBundleTotalSize() {
    const selectedInput = document.querySelector('input[name="bundle-size"]:checked');
    if (!selectedInput) return 0;

    const selectedValue = selectedInput.value;
    return selectedValue || 0;
  }

  _hasSellingPlans() {
    return (
      this._sellingPlanGroup &&
      typeof this._sellingPlanGroup === "object" &&
      Object.keys(this._sellingPlanGroup).length > 0
    );
  }

  _addSellingPlanPercent() {
    setTimeout(() => {
      // The double block earns the larger sizeInterval's tier, so each block works out its own
      // headline percentage from the size it actually buys.
      const sellingPlansBlocks = document.querySelectorAll(".skio-bundle-builder__purchase-option--selling-plans");
      const oneTimeBlock = document.querySelector(".skio-bundle-builder__purchase-option--one-time");

      const bundleTotalSize = this._getSelectedBundleTotalSize();
      const bundlePercent = this._getDiscountPercent(bundleTotalSize);
      const doubleSize = this._getDoubleSubscriptionSize();
      let totalPercent;

      sellingPlansBlocks.forEach((sellingPlansBlock) => {
        const isDoubleBlock = sellingPlansBlock.classList.contains(
          "skio-bundle-builder__purchase-option--double-selling-plans",
        );
        const blockSize = isDoubleBlock ? doubleSize || bundleTotalSize : bundleTotalSize;
        const blockPercent = this._getDiscountPercent(blockSize, true);
        const addedCompareMultiplier = isDoubleBlock && bundleTotalSize ? blockSize / bundleTotalSize : 1;

        const percentContainer = sellingPlansBlock.querySelector("[data-total-percent]");
        if (percentContainer) {
          const allVariants = this._getAllVariants(this._selectableProductVariants);
          const cheapestVariant = this._getCheapestVariant(allVariants);
          const cheapestAdjustedVariant = this._getCheapestAdjustedVariant(this._sellingPlanGroup);
          if (cheapestVariant && cheapestAdjustedVariant) {
            const subCompare =
              cheapestVariant.price * blockSize + this._getSubscriptionAddedCompare(addedCompareMultiplier);
            const adjustedPrice =
              cheapestAdjustedVariant.adjustedPrice > 0 ? cheapestAdjustedVariant.adjustedPrice : cheapestVariant.price;
            const unitPrice = this._roundMoney(adjustedPrice);
            const unitDiscount = this._floorMoney(unitPrice * (blockPercent / 100));
            const subDefault = (unitPrice - unitDiscount) * blockSize;
            const calculatedPercent = subCompare > 0 ? Math.round(((subCompare - subDefault) / subCompare) * 100) : 0;
            percentContainer.textContent = `${calculatedPercent}%`;
          }
        }
      });

      if (oneTimeBlock) {
        totalPercent = bundlePercent;
        const percentContainer = oneTimeBlock.querySelector("[data-total-percent]");
        if (percentContainer) {
          percentContainer.textContent = `${totalPercent}%`;
        }
      }
    }, 50);
  }

  _renderPriceContainer(
    cheapestVariant,
    size,
    priceBlock,
    showFrom = true,
    isSubscription = false,
    isOneTime = false,
    isDouble = false,
  ) {
    const cheapestAdjustedVariant = this._getCheapestAdjustedVariant(this._sellingPlanGroup);
    priceBlock.innerHTML = "";

    if (isSubscription) {
      priceBlock.setAttribute(isDouble ? "data-double-subscription-price" : "data-subscription-price", "");
    }

    const comparePrice = document.createElement("span");
    comparePrice.classList.add("price-compare");

    // For the double option `size` is already the larger sizeInterval, so the unit count and the
    // discount tier below both come straight from it. Only the per-box added-compare has to know
    // how many of the picked box this represents.
    const totalUnits = size;
    const pickedSize = parseInt(this._getSelectedBundleTotalSize());
    const addedCompareMultiplier = isDouble && pickedSize ? size / pickedSize : 1;

    let comparePriceValue = "";
    if (cheapestVariant.compareAtPrice != null) {
      comparePriceValue =
        cheapestVariant.compareAtPrice > cheapestVariant.price
          ? cheapestVariant.compareAtPrice * totalUnits
          : cheapestVariant.price * totalUnits;
    } else {
      comparePriceValue = cheapestVariant.price * totalUnits;
    }
    if (isSubscription) comparePriceValue += this._getSubscriptionAddedCompare(addedCompareMultiplier);

    if (isOneTime) {
      comparePrice.textContent = `${this._formatMoney(comparePriceValue)}`;
    } else {
      comparePrice.textContent = comparePriceValue ? `${this._formatMoney(comparePriceValue)}` : "";
    }

    const price = document.createElement("span");
    price.classList.add("price-default");

    const percent = this._getDiscountPercent(size, isSubscription);

    let itemPrice;
    let basePrice;

    if (isSubscription) {
      const adjustedPrice =
        cheapestAdjustedVariant?.adjustedPrice > 0 ? cheapestAdjustedVariant.adjustedPrice : cheapestVariant.price;

      itemPrice = this._roundMoney(adjustedPrice);

      const rawUnitDiscount = itemPrice * (percent / 100);
      const unitDiscount = this._floorMoney(rawUnitDiscount);

      itemPrice = itemPrice - unitDiscount;
      basePrice = itemPrice * totalUnits;
    } else if (isOneTime) {
      itemPrice = this._roundMoney(cheapestVariant.price);

      const rawUnitDiscount = itemPrice * (percent / 100);
      const unitDiscount = this._floorMoney(rawUnitDiscount);

      itemPrice = itemPrice - unitDiscount;
      basePrice = itemPrice * totalUnits;
    } else {
      itemPrice = cheapestVariant.price;
      basePrice = itemPrice * totalUnits;
    }

    if (comparePriceValue <= basePrice) {
      comparePrice.textContent = "";
    }

    price.textContent = `${showFrom ? "From " : ""}${this._formatMoney(basePrice)}`;

    priceBlock.appendChild(price);
    priceBlock.appendChild(comparePrice);
  }

  _calculateSubscriptionDiscountSum(cheapestVariant, cheapestAdjustedVariant, size, addedCompareMultiplier = 1) {
    const normalPrice = cheapestVariant.price * size + this._getSubscriptionAddedCompare(addedCompareMultiplier);

    const adjustedPrice =
      cheapestAdjustedVariant && cheapestAdjustedVariant.adjustedPrice > 0
        ? cheapestAdjustedVariant.adjustedPrice
        : cheapestVariant.price;

    const subscriptionPercent = this._getDiscountPercent(size, true);
    const unitPrice = this._roundMoney(adjustedPrice);
    const unitDiscount = this._floorMoney(unitPrice * (subscriptionPercent / 100));
    const subscriptionPrice = (unitPrice - unitDiscount) * size;

    const difference = normalPrice - subscriptionPrice;

    const basePercent = normalPrice > 0 ? Math.round((difference / normalPrice) * 100) : 0;
    const percent = basePercent + subscriptionPercent;

    return {
      normalPrice,
      subscriptionPrice,
      difference,
      percent,
    };
  }

  _isSellingPlanSelected() {
    const selectedPlan = document.querySelector('input[data-radio-type="selling_plan"]:checked');
    return !!selectedPlan;
  }

  _updateIntervalPriceBlock(block) {
    if (!block) return;
    const subscriptionInput = this._isSellingPlanSelected();
    const bundleInputs = block.querySelectorAll('input[name="bundle-size"]');

    // Linked cards price off another bundle entirely, so they're handled separately (and before
    // the early return below, which only concerns this bundle's own variants).
    this._updateLinkedIntervalCards(block);

    const allVariants = this._getAllVariants(this._selectableProductVariants);
    const cheapestVariant = this._getCheapestVariant(allVariants);
    if (!cheapestVariant) return;

    const cheapestAdjustedVariant = this._hasSellingPlans()
      ? this._getCheapestAdjustedVariant(this._sellingPlanGroup)
      : null;

    const intervals = JSON.parse(block.dataset.intervals);
    const isDouble = this._isDoubleSubscriptionSelected();

    bundleInputs.forEach((input) => {
      const intervalBadge = input.closest("label")?.querySelector(".skio-bundle-builder__interval-badge");
      const intervalDiscountBlock = input.closest("label")?.querySelector(".discount-block");
      const size = parseFloat(input.value);
      const priceDefault = input.closest("label")?.querySelector(".price-default");
      const priceCompare = input.closest("label")?.querySelector(".price-compare");
      const intervalOpt = intervals.find((opt) => opt.size == size && !opt.linked_bundle_id);

      // While double is selected every card previews its own doubled price, so switching size
      // keeps showing what you would actually pay. Cards with no larger sizeInterval stay as-is.
      const doubleSize = isDouble ? this._getDoubleSubscriptionSize(size) : null;
      const pricingSize = doubleSize || size;
      const addedCompareMultiplier = doubleSize ? doubleSize / size : 1;

      const percent = this._getDiscountPercent(pricingSize, subscriptionInput);

      if (priceCompare) {
        const compareTotal =
          cheapestVariant.price * pricingSize +
          (subscriptionInput ? this._getSubscriptionAddedCompare(addedCompareMultiplier) : 0);
        priceCompare.textContent = `${this._formatMoney(compareTotal)}`;
      }

      if (priceDefault) {
        let unitPrice = subscriptionInput
          ? this._roundMoney(cheapestAdjustedVariant?.adjustedPrice ?? cheapestVariant.price)
          : this._roundMoney(cheapestVariant.price);

        const rawUnitDiscount = unitPrice * (percent / 100);
        const unitDiscount = this._floorMoney(rawUnitDiscount);

        const finalUnitPrice = unitPrice - unitDiscount;
        const discountedPrice = finalUnitPrice * pricingSize;

        priceDefault.textContent = `${this._formatMoney(discountedPrice)}`;
      }

      if (intervalBadge) {
        let unitPriceForBadge = subscriptionInput
          ? this._roundMoney(cheapestAdjustedVariant?.adjustedPrice ?? cheapestVariant.price)
          : this._roundMoney(cheapestVariant.price);

        const rawUnitDiscount = unitPriceForBadge * (percent / 100);
        const unitDiscount = this._floorMoney(rawUnitDiscount);
        const finalUnitPrice = unitPriceForBadge - unitDiscount;

        const intervalPrice = finalUnitPrice / this._mealQuantity;
        const badgeText = input.closest("[data-box-interval]").getAttribute("data-badge-text");

        intervalBadge.textContent = `${this._formatMoney(intervalPrice)} ${badgeText}`;
      }

      if (intervalDiscountBlock) {
        let savingsAmount = 0;
        if (subscriptionInput) {
          const { difference } = this._calculateSubscriptionDiscountSum(
            cheapestVariant,
            cheapestAdjustedVariant,
            pricingSize,
            addedCompareMultiplier,
          );
          savingsAmount = difference;
        } else {
          const unitPrice = this._roundMoney(cheapestVariant.price);
          const unitDiscount = this._floorMoney(unitPrice * (percent / 100));
          savingsAmount = unitDiscount * pricingSize;
        }

        intervalDiscountBlock.innerHTML =
          savingsAmount > 0 ? `<span>Save</span> ${this._formatMoney(savingsAmount)}` : "";
      }
    });
  }

  _getVariantPriceById(variantId) {
    const allVariants = this._getAllVariants(this._selectableProductVariants);
    if (!v.platformId) return false;
    const variant = allVariants.find((v) => v.platformId.split("/").pop() === variantId);
    if (!variant) return 0;
    return variant.price;
  }

  _applySellingPlanPriceToVariant(variantId, selectedSellingPlanId, adjustedPrice) {
    if (!variantId || !selectedSellingPlanId) return;

    const selectedPlanGid = Object.keys(this._sellingPlanGroup).find((planGid) =>
      planGid.endsWith(selectedSellingPlanId),
    );
    if (!selectedPlanGid) return;

    const planEntry = this._sellingPlanGroup[selectedPlanGid];
    if (!planEntry) return;

    const mappingKey = Object.keys(planEntry.variantMapping).find((gid) => gid.endsWith(variantId));
    if (!mappingKey) return;

    const planData = planEntry.variantMapping[mappingKey];
    if (!planData) return;

    const priceToUse = adjustedPrice ?? planData.adjustedPrice;

    const bundleItem = this._bundleItems.find((i) => i.id === variantId);
    if (bundleItem) {
      bundleItem.price = priceToUse;
      bundleItem.planId = planData.planId;
    }

    this._updateSubtotalSumBlock();
  }

  _getCurrentPriceData(variantId) {
    const numericVariantId = variantId;

    const variant = this._getAllVariants(this._selectableProductVariants).find(
      (v) => v.platformId?.split("/").pop() === numericVariantId,
    );

    if (!this._hasSellingPlans()) {
      return {
        price: variant?.price ?? 0,
        planId: null,
        type: "one_time_purchase",
      };
    }

    const selectedPlanInput = document.querySelector('input[name="purchase-option"]:checked');

    if (selectedPlanInput) {
      if (selectedPlanInput.dataset.radioType === "selling_plan") {
        const selectedSellingPlanId = selectedPlanInput.value;
        const selectedPlanGid = Object.keys(this._sellingPlanGroup).find((planGid) =>
          planGid.endsWith(selectedSellingPlanId),
        );
        if (selectedPlanGid) {
          const planData = this._sellingPlanGroup[selectedPlanGid];
          const mappingEntry = Object.entries(planData.variantMapping).find(
            ([gid, mapping]) =>
              gid.endsWith(numericVariantId) && mapping.planId.split("/").pop() === selectedSellingPlanId,
          );

          if (mappingEntry) {
            return {
              price: mappingEntry[1].adjustedPrice,
              planId: selectedSellingPlanId,
              type: "selling_plan",
            };
          }
        }
      } else if (selectedPlanInput.dataset.radioType === "one_time_purchase") {
        if (variant) {
          return {
            price: variant.price,
            planId: null,
            type: "one_time_purchase",
          };
        }
      }
    }

    return {
      price: variant?.price ?? 0,
      planId: null,
      type: "one_time_purchase",
    };
  }

  _getComparePriceData(variantId) {
    const numericVariantId = variantId;
    const selectedPlanInput = document.querySelector('input[name="purchase-option"]:checked');

    const allVariants = this._getAllVariants(this._selectableProductVariants);
    const variant = allVariants.find((v) => v.platformId.split("/").pop() === numericVariantId);
    if (!variant) return { comparePrice: 0 };
    if (!this._hasSellingPlans()) {
      return {
        comparePrice: variant.compareAtPrice || 0,
      };
    }

    if (selectedPlanInput) {
      if (selectedPlanInput.dataset.radioType === "selling_plan") {
        const selectedSellingPlanId = selectedPlanInput.value;
        const selectedPlanGid = Object.keys(this._sellingPlanGroup).find((planGid) =>
          planGid.endsWith(selectedSellingPlanId),
        );
        if (selectedPlanGid) {
          const planData = this._sellingPlanGroup[selectedPlanGid];
          const mappingEntry = Object.entries(planData.variantMapping).find(
            ([gid, mapping]) =>
              gid.endsWith(numericVariantId) && mapping.planId.split("/").pop() === selectedSellingPlanId,
          );

          if (mappingEntry) {
            return { comparePrice: variant.price };
          }
        }
      } else if (selectedPlanInput.dataset.radioType === "one_time_purchase") {
        if (variant.compareAtPrice && variant.compareAtPrice > 0) {
          return { comparePrice: variant.compareAtPrice };
        }
      }
    }

    return { comparePrice: variant.compareAtPrice || 0 };
  }

  _setupPurchaseOptionListener() {
    const purchaseOptionInputs = document.querySelectorAll('input[name="purchase-option"]');

    purchaseOptionInputs.forEach((input) => {
      const onChange = () => {
        const block = document.querySelector("[data-box-interval]");
        this._updateIntervalPriceBlock(block);
        this._updateBundleItemsForPurchaseOption();
        this._updateDiscountBlockTop();
      };
      input.addEventListener("change", onChange);
      this._listeners.push({ el: input, type: "change", fn: onChange });
    });
  }

  _updatePurchasePriceBlock(block, size) {
    if (!block) return;
    const purchasePriceBlocks = block.querySelectorAll(".skio-bundle-builder__price-block");
    const totalDiscountBlocks = block.querySelectorAll("[data-total-discount]");

    const allVariants = this._getAllVariants(this._selectableProductVariants);
    const cheapestVariant = this._getCheapestVariant(allVariants);
    const cheapestAdjustedVariant = this._getCheapestAdjustedVariant(this._sellingPlanGroup);

    if (!cheapestVariant) return;

    const doubleSize = this._getDoubleSubscriptionSize();

    purchasePriceBlocks.forEach((purchaseBlock) => {
      const isDoublePrice = purchaseBlock.hasAttribute("data-double-subscription-price");
      const subscriptionPrice = purchaseBlock.hasAttribute("data-subscription-price") || isDoublePrice;
      // The double block prices as a box of the next sizeInterval up, tier included.
      const priceSize = isDoublePrice ? doubleSize || size : size;
      const addedCompareMultiplier = isDoublePrice && size ? priceSize / size : 1;
      const priceDefault = purchaseBlock?.querySelector(".price-default");
      const priceCompare = purchaseBlock?.querySelector(".price-compare");
      const cheapestSum = cheapestVariant.price * priceSize + this._getSubscriptionAddedCompare(addedCompareMultiplier);

      if (priceCompare) {
        priceCompare.textContent = subscriptionPrice ? `${this._formatMoney(cheapestSum)}` : "";
      }

      const discountMultiplier = this._getDiscountMultiplier(priceSize, subscriptionPrice);

      let displayPrice;
      if (subscriptionPrice) {
        const adjustedPrice =
          cheapestAdjustedVariant && cheapestAdjustedVariant.adjustedPrice > 0
            ? cheapestAdjustedVariant.adjustedPrice
            : cheapestVariant.price;

        displayPrice = adjustedPrice * priceSize * discountMultiplier;
      } else {
        displayPrice = cheapestVariant.price * priceSize * discountMultiplier;
      }

      if (priceDefault) {
        priceDefault.textContent = `${this._formatMoney(displayPrice.toFixed(2))}`;
      }

      if (totalDiscountBlocks.length) {
        totalDiscountBlocks.forEach((totalDiscountBlock) => {
          const input = totalDiscountBlock?.closest("label")?.querySelector("input");
          const priceAdjustmentAmount = input?.getAttribute("data-price-adjustment-amount");
          // These blocks cover both purchase types, and volume discounts only apply to
          // subscriptions, so each label is resolved from its own radio.
          const bundleDiscount = this._getDiscountPercent(size, input?.dataset.radioType === "selling_plan");
          const totalDiscountAmount = +priceAdjustmentAmount + bundleDiscount;
          totalDiscountBlock.textContent = totalDiscountAmount;
        });
      }
    });

    // Scoped per option block so the double block's dropdown doesn't overwrite the normal one's label.
    block.querySelectorAll(".skio-bundle-builder__purchase-option--selling-plans").forEach((optionBlock) => {
      const checkedRadio = optionBlock.querySelector('input[type="radio"][data-radio-type="selling_plan"]:checked');
      if (!checkedRadio) return;

      const isDoubleBlock = optionBlock.classList.contains(
        "skio-bundle-builder__purchase-option--double-selling-plans",
      );
      const bundleDiscount = this._getDiscountPercent(isDoubleBlock ? doubleSize || size : size, true);
      const priceAdjustmentAmount = +checkedRadio.dataset.priceAdjustmentAmount;
      const totalDiscountAmount = priceAdjustmentAmount + bundleDiscount;

      const selectedBlock = optionBlock.querySelector(".custom-dropdown .selected");
      if (selectedBlock) {
        let discountSpan = selectedBlock.querySelector("[data-total-discount]");
        if (!discountSpan) {
          discountSpan = document.createElement("span");
          discountSpan.dataset.totalDiscount = "";
          selectedBlock.appendChild(discountSpan);
        }
        discountSpan.textContent = totalDiscountAmount;
      }
    });
  }

  _updateSubscriptionSaveBlock(saveBlock, cheapestVariant, cheapestAdjustedVariant, size) {
    if (!saveBlock || !cheapestVariant || !cheapestAdjustedVariant) return;

    const { difference } = this._calculateSubscriptionDiscountSum(cheapestVariant, cheapestAdjustedVariant, size);
    const subscruptionSaveBlockText =
      saveBlock.closest("[data-selling-plans]")?.getAttribute("data-badge-discount-text") || "";

    saveBlock.textContent = `${subscruptionSaveBlockText} ${this._formatMoney(difference)}`;
  }

  _getCheckedRadioValue(name) {
    const radios = document.querySelectorAll(`input[name="${name}"]`);
    for (const radio of radios) {
      if (radio.checked) {
        return radio.value;
      }
    }
    return null;
  }

  _getMaxNumber(numbers) {
    if (numbers.length === 0) {
      return null;
    }
    let maxValue = numbers[0];
    for (let i = 1; i < numbers.length; i++) {
      if (numbers[i] > maxValue) {
        maxValue = numbers[i];
      }
    }
    return maxValue;
  }

  _isPrimaryCard(card, variantId) {
    const cards = Array.from(
      document.querySelectorAll(`.product-card[data-active-variant-id="${variantId}"]:not(.product-card--upsell)`),
    );

    return cards[0] === card;
  }

  _syncCardWithBundleState(card, variantId) {
    const bundleItem = this._bundleItems.find((i) => i.id === variantId);
    const cardBottom = card.querySelector(".product-card__bottom");
    const selector = cardBottom?.querySelector(".quantity-selector");

    if (bundleItem && bundleItem.quantity > 0 && this._isPrimaryCard(card, variantId)) {
      if (selector) {
        const display = selector.querySelector(".quantity-display");
        if (display) {
          display.textContent = bundleItem.quantity;
        }
      } else {
        const variant = card._variantData;
        const { container, display } = this._createBundleQuantitySelector(card, variant, bundleItem.quantity);

        const addButton = cardBottom.querySelector(".product-card__button");
        if (addButton) {
          cardBottom.removeChild(addButton);
        }

        cardBottom.appendChild(container);
      }
    } else {
      if (selector) {
        cardBottom.removeChild(selector);

        const variant = card._variantData;
        this._recreateAddButton(card, cardBottom, variant);
      }
    }
  }

  _syncAllCardsAfterSizeChange() {
    const cards = document.querySelectorAll(".product-card:not(.product-card--upsell)");

    cards.forEach((card) => {
      const variantId = card.dataset.activeVariantId;
      if (variantId) {
        this._syncCardWithBundleState(card, variantId);
      }
    });
  }

  _setupIntervalOptionListener() {
    const inputs = document.querySelectorAll('input[name="bundle-size"]');
    const saveBlock = document.querySelector(".skio-bundle-builder__selling-plans-save");
    const cheapestVariant = this._getCheapestSelectableVariant();
    const cheapestAdjusted = this._getCheapestAdjustedVariant(this._sellingPlanGroup);

    if (inputs.length) {
      inputs.forEach((input) => {
        const onChange = () => {
          const size = parseInt(input.value);
          const totals = this._getBundleTotals();
          const totalQuantity = totals.totalQuantity;

          if (size < totalQuantity) {
            const diff = totalQuantity - size;

            this._bundleItems = this._reduceQuantityFromEnd(this._bundleItems, diff);

            this._syncAllCardsAfterSizeChange();
          }

          const purchaseBlock = document.querySelector("[data-selling-plans]");
          if (purchaseBlock) {
            this._updatePurchasePriceBlock(purchaseBlock, size);
          }

          const subscriptionPriceBlock = document.querySelector(".skio-bundle-builder__price-block.subscription-price");
          if (subscriptionPriceBlock) {
            this._renderPriceContainer(cheapestVariant, size, subscriptionPriceBlock, false, true);
          }

          const doubleSubscriptionPriceBlock = document.querySelector(
            ".skio-bundle-builder__price-block.double-subscription-price",
          );
          const doubleSize = this._getDoubleSubscriptionSize();
          if (doubleSubscriptionPriceBlock && doubleSize) {
            this._renderPriceContainer(
              cheapestVariant,
              doubleSize,
              doubleSubscriptionPriceBlock,
              false,
              true,
              false,
              true,
            );
          }

          const oneTimePriceBlock = document.querySelector(".skio-bundle-builder__price-block.one-time-price");
          if (oneTimePriceBlock) {
            this._renderPriceContainer(cheapestVariant, size, oneTimePriceBlock, false, false, true);
          }

          if (saveBlock) {
            this._updateSubscriptionSaveBlock(saveBlock, cheapestVariant, cheapestAdjusted, size);
          }

          this._addSellingPlanPercent();
          this._updateDiscountBlockTop();
          this._updateRemainingQuantityBlock();
          this._toggleCheckoutButton();
          this._updateBundleControls(size);
          this._updateUpsellButtonsState();
          this._updateMainPrice();

          const currentSelection = document.querySelector('input[name="purchase-option"]:checked');
          const wasOneTime = currentSelection?.dataset.radioType === "one_time_purchase";
          const wasDoubleSubscription = currentSelection?.dataset.doubleSubscription === "true";

          this._renderSellingPlans(this._sellingPlanGroup);

          if (wasDoubleSubscription) {
            // Re-rendering resets to the normal subscription, so re-pick the double option.
            const doubleBlock = this._getDoubleSellingPlansBlock();
            const oneTimeBlock = document.querySelector(".skio-bundle-builder__purchase-option--one-time");
            if (doubleBlock) {
              this._handleSellingPlansSelection(
                doubleBlock,
                oneTimeBlock,
                document.querySelector("[data-box-interval]"),
              );
            }
          }

          if (wasOneTime) {
            const oneTimeRadio = document.querySelector(
              'input[name="purchase-option"][data-radio-type="one_time_purchase"]',
            );
            if (oneTimeRadio) {
              oneTimeRadio.checked = true;

              document.querySelectorAll('input[data-radio-type="selling_plan"]').forEach((radio) => {
                radio.checked = false;
              });

              const selected = document.querySelector(".custom-dropdown .selected");
              if (selected) {
                selected.textContent = "Select a plan";
              }
            }
          }

          this._renderProgressBarBlocks();
          this._updateStickyProgressBarBlocks();
          this._renderStickyProgressBarBlocks();
          this._updateStickyRemainingQuantityBlock();
          this._updateSubtotalSumBlock();
          this._updateMediaBySize(true);

          this._updateBundleItemsForPurchaseOption();
          this._updatePaymentBlock();
        };

        input.addEventListener("change", onChange);
        this._listeners.push({ el: input, type: "change", fn: onChange });
      });
    }
  }

  _resetBundleSelector(card, variant) {
    const cardBottom = card.querySelector(".product-card__bottom");
    const addButton = card.querySelector(".product-card__button");

    const existingSelector = cardBottom.querySelector(".quantity-selector");
    if (existingSelector) {
      cardBottom.removeChild(existingSelector);
      const prevVariantId = existingSelector.dataset.variantId;
      this._bundleItems = this._bundleItems.filter((i) => i.id !== prevVariantId);
    }

    if (!cardBottom.contains(addButton)) {
      const addButton = document.createElement("button");
      addButton.classList.add("product-card__button", "button", "button--destructive", "add-button");
      addButton.dataset.label = this._addToCartText;
      addButton.setAttribute("aria-label", this._addToCartText);
      addButton.innerHTML = this._addToCartText;
      addButton.dataset.inCart = "false";

      const onAddButtonClick = (evt) => this._handleAddButtonClick(evt, card, variant);
      addButton.addEventListener("click", onAddButtonClick);
      this._listeners.push({ el: addButton, type: "click", fn: onAddButtonClick });

      cardBottom.appendChild(addButton);
    }
  }

  _createVariantDropdown(variant, card, variantButton, cardBottom, image) {
    const dropdown = document.createElement("ul");
    dropdown.classList.add("product-card__dropdown");
    dropdown.style.display = "none";

    const onButtonClick = () => {
      document.querySelectorAll(".product-card__dropdown").forEach((drop) => {
        if (drop !== dropdown) drop.style.display = "none";
      });
      dropdown.style.display = dropdown.style.display === "none" ? "block" : "none";
    };
    variantButton.addEventListener("click", onButtonClick);
    this._listeners.push({ el: variantButton, type: "click", fn: onButtonClick });

    variant.productVariants.forEach((v) => {
      const item = document.createElement("li");
      item.textContent = v.productVariantTitle;

      const onItemClick = () => {
        card.selectedVariant = v;
        const variantId = v.platformId.split("/").pop();
        card.dataset.activeVariantId = variantId;
        variantButton.innerHTML = v.productVariantTitle;
        image.src = v.imageSrc || variant.productImageSrc;

        const priceData = this._getCurrentPriceData(variantId);
        card.querySelector(".product-card__price").textContent = `$${priceData.price.toFixed(2)}`;

        const compareData = this._getComparePriceData(variantId);
        const priceCompare = card.querySelector(".product-card__price-compare");
        priceCompare.innerHTML = "";
        if (compareData.comparePrice > 0) {
          const span = document.createElement("span");
          span.textContent = `$${compareData.comparePrice.toFixed(2)}`;
          priceCompare.appendChild(span);
        }

        dropdown.style.display = "none";

        const oldSelector = cardBottom.querySelector(".quantity-selector");
        if (oldSelector) cardBottom.removeChild(oldSelector);
        const oldButton = cardBottom.querySelector(".product-card__button");
        if (oldButton) cardBottom.removeChild(oldButton);

        const addButton = document.createElement("button");
        addButton.classList.add("product-card__button", "button", "button--destructive");
        addButton.dataset.label = this._addToCartText;
        addButton.setAttribute("aria-label", this._addToCartText);
        addButton.innerHTML = this._addToCartText;
        cardBottom.appendChild(addButton);

        addButton.onclick = () => {
          const selected = card.selectedVariant || v;
          const { container } = this._createBundleQuantitySelector(card, { productVariants: [selected] }, 1);
          container.dataset.variantId = this._getVariantId(selected);

          if (cardBottom.contains(addButton)) {
            cardBottom.replaceChild(container, addButton);
          } else {
            cardBottom.appendChild(container);
          }
        };
      };

      item.addEventListener("click", onItemClick);
      this._listeners.push({ el: item, type: "click", fn: onItemClick });

      dropdown.appendChild(item);
    });

    return dropdown;
  }

  _sortVariantsByWeight(variants) {
    const extractWeightInGrams = (title) => {
      const match = title.match(/(\d+)\s*(kg|g)/i);
      if (!match) return 0;
      const value = parseFloat(match[1]);
      const unit = match[2].toLowerCase();
      return unit === "kg" ? value * 1000 : value;
    };

    return variants.sort(
      (a, b) => extractWeightInGrams(a.productVariantTitle) - extractWeightInGrams(b.productVariantTitle),
    );
  }

  _applyPriceToVariantCard(variantId) {
    const priceData = this._getCurrentPriceData(variantId);
    const bundleItem = this._bundleItems.find((i) => i.id === variantId);

    if (bundleItem) {
      bundleItem.price = priceData.price;
      bundleItem.planId = priceData.planId;
    }

    const priceElement = document.querySelector(
      `.product-card[data-active-variant-id="${variantId}"] .product-card__price`,
    );
    if (priceElement) priceElement.textContent = `$${priceData.price.toFixed(2)}`;
  }

  _handleAddButtonClick(evt, card, variant) {
    evt.preventDefault();

    const selected = card.selectedVariant || variant.productVariants[0];

    const cardBottom = card.querySelector(".product-card__bottom");
    const existingSelector = cardBottom.querySelector(".quantity-selector");
    if (existingSelector) {
      cardBottom.removeChild(existingSelector);
    }

    const result = this._createBundleQuantitySelector(card, { productVariants: [selected] }, 1);

    if (result && result.container instanceof HTMLElement) {
      const addButton = cardBottom.querySelector(".product-card__button");
      if (addButton && cardBottom.contains(addButton)) {
        cardBottom.replaceChild(result.container, addButton);
      } else {
        cardBottom.appendChild(result.container);
      }
    }
  }

  async _fetchBundleImageByDefaultVariant(variantId) {
    const query = `
      query ($id: ID!) {
        node(id: $id) {
          ... on ProductVariant {
            id
            metafield(namespace: "custom", key: "bundle_builder_image") {
              reference {
                ... on MediaImage {
                  image {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch("/api/2024-01/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": "ae3098bf902396fd7f396049b17d60fb",
        },
        body: JSON.stringify({
          query,
          variables: { id: variantId },
        }),
      });

      const json = await response.json();

      return json?.data?.node?.metafield?.reference?.image || null;
    } catch (error) {
      console.error("Error fetching bundle image:", error);
      return null;
    }
  }

  _createProductCard(variant) {
    const card = document.createElement("div");
    card.classList.add("product-card");
    card._variantData = variant;

    variant.productVariants = this._sortVariantsByWeight(variant.productVariants);

    const cardVariantId = variant.productVariants[0].platformId.split("/").pop();

    card.dataset.activeVariantId = cardVariantId;

    let selectedVariant = variant.productVariants[0];
    card.selectedVariant = selectedVariant;

    const cardContent = document.createElement("div");
    cardContent.classList.add("product-card__content");

    const title = document.createElement("h3");
    title.classList.add("skio-bundle-builder__title-heading", "poppins-heading");

    const titleText = variant.displayTitle || this._cleanProductTitle(variant.productTitle);

    title.innerHTML = `<span>${titleText}</span>`;

    const cardBottom = document.createElement("div");
    cardBottom.classList.add("product-card__bottom");

    const image = document.createElement("img");
    image.alt = "";
    card._bundleImageElement = image;

    const variantContainer = document.createElement("div");
    variantContainer.classList.add("product-card__variant-container");

    if (variant.productVariants.length > 1) {
      card.selectedVariant = selectedVariant;

      const variantButton = document.createElement("button");
      variantButton.classList.add("product-card__variant-button");
      variantButton.innerHTML = selectedVariant.productVariantTitle;

      const svgWrapper = document.createElement("span");
      svgWrapper.classList.add("product-card__variant-icon");
      svgWrapper.innerHTML = this._svgDropdown;

      const dropdown = this._createVariantDropdown(variant, card, variantButton, cardBottom, image);

      variantContainer.appendChild(variantButton);
      variantContainer.appendChild(svgWrapper);
      variantContainer.appendChild(dropdown);
    }

    const addButton = document.createElement("button");
    addButton.classList.add("product-card__button", "button", "button--destructive", "add-button");
    addButton.dataset.label = this._addToCartText;
    addButton.setAttribute("aria-label", this._addToCartText);
    addButton.innerHTML = this._addToCartText;
    addButton.dataset.inCart = "false";

    const onAddButtonClick = (evt) => this._handleAddButtonClick(evt, card, variant);
    addButton.addEventListener("click", onAddButtonClick);
    this._listeners.push({ el: addButton, type: "click", fn: onAddButtonClick });

    card.appendChild(image);
    card.appendChild(cardContent);
    cardContent.appendChild(title);
    cardContent.appendChild(variantContainer);
    cardContent.appendChild(cardBottom);
    cardBottom.appendChild(addButton);

    (async () => {
      const variantGID = variant.productVariants[0].platformId;
      let loaded = false;

      try {
        const bundleImage = await this._fetchBundleImageByDefaultVariant(variantGID);
        if (bundleImage?.url) {
          image.src = bundleImage.url;
          image.alt = bundleImage.altText || selectedVariant.productTitle;
          loaded = true;
        }
      } catch (err) {
        console.error("Error fetching bundle image", err);
      }

      if (!loaded) {
        image.src = selectedVariant.imageSrc || variant.productImageSrc;
        image.alt = selectedVariant.productTitle;
      }
    })();

    return card;
  }

  _isBundleReady() {
    const { totalQuantity } = this._getBundleTotals();
    const size = this._getSelectedBundleTotalSize();
    return totalQuantity >= size;
  }

  _updateUpsellButtonsState() {
    document.querySelectorAll(".product-card--upsell").forEach((card) => {
      const button = card.querySelector(".add-button");
      if (!button) return;

      const isReady = this._isBundleReady();
      button.classList.toggle("is-disabled", !isReady);
    });

    document.querySelectorAll(".product-card--upsell .quantity-selector .plus").forEach((plusBtn) => {
      plusBtn.disabled = false;
      plusBtn.classList.remove("disabled");
    });

    document.querySelectorAll(".product-card--upsell .quantity-selector .minus").forEach((minusBtn) => {
      minusBtn.disabled = false;
      minusBtn.classList.remove("disabled");
    });
  }

  async _fetchUpsellProductImage(handle) {
    const query = `
      query ($handle: String!) {
        product(handle: $handle) {
          metafield(namespace: "custom", key: "single_flavour_product_image") {
            reference {
              ... on MediaImage {
                image {
                  url
                  altText
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch("/api/2024-01/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": "ae3098bf902396fd7f396049b17d60fb",
        },
        body: JSON.stringify({
          query,
          variables: { handle },
        }),
      });

      const json = await response.json();

      return json?.data?.product?.metafield?.reference?.image || null;
    } catch (error) {
      console.error("Error fetching upsell product image:", error);
      return null;
    }
  }

  _createUpsellCard(variant) {
    const card = document.createElement("div");
    card.classList.add("product-card", "product-card--upsell");
    card._variantData = variant;
    card._isUpsell = true;

    variant.productVariants = this._sortVariantsByWeight(variant.productVariants);

    const cardVariantId = variant.productVariants[0].platformId.split("/").pop();
    card.dataset.activeVariantId = cardVariantId;

    let selectedVariant = variant.productVariants[0];
    card.selectedVariant = selectedVariant;

    const cardContent = document.createElement("div");
    cardContent.classList.add("product-card__content");

    const title = document.createElement("h3");
    title.classList.add("skio-bundle-builder__title-heading", "poppins-heading");
    let productTitle = variant.productTitle || "";
    productTitle = productTitle.startsWith("Dukes ") ? productTitle.replace(/^Dukes\s+/, "") : productTitle;
    productTitle = productTitle.replace("Shake", "").replace("Soup", "").replace("Oats", "");
    title.innerHTML = `<span>${productTitle}</span>`;

    const cardBottom = document.createElement("div");
    cardBottom.classList.add("product-card__bottom");

    const priceContainer = document.createElement("div");
    priceContainer.classList.add("product-card__price-container");

    const price = document.createElement("p");
    price.classList.add("product-card__price");
    price.textContent = this._formatMoney(selectedVariant.price);

    priceContainer.appendChild(price);
    cardBottom.appendChild(priceContainer);

    const image = document.createElement("img");
    image.alt = "";
    card._upsellImageElement = image;

    const variantContainer = document.createElement("div");
    variantContainer.classList.add("product-card__variant-container");

    if (variant.productVariants.length > 1) {
      const variantButton = document.createElement("button");
      variantButton.classList.add("product-card__variant-button");
      variantButton.innerHTML = selectedVariant.productVariantTitle;

      const svgWrapper = document.createElement("span");
      svgWrapper.classList.add("product-card__variant-icon");
      svgWrapper.innerHTML = this._svgDropdown;

      const dropdown = this._createUpsellVariantDropdown(variant, card, variantButton, cardBottom, image);

      variantContainer.appendChild(variantButton);
      variantContainer.appendChild(svgWrapper);
      variantContainer.appendChild(dropdown);
    }

    const addButton = document.createElement("button");
    addButton.classList.add("product-card__button", "button", "button--destructive", "add-button");
    addButton.dataset.label = this._addToCartText;
    addButton.setAttribute("aria-label", this._addToCartText);
    addButton.innerHTML = this._addToCartText;
    addButton.dataset.inCart = "false";

    const onAddButtonClick = (evt) => this._handleUpsellAddButtonClick(evt, card, variant);
    addButton.addEventListener("click", onAddButtonClick);
    this._listeners.push({ el: addButton, type: "click", fn: onAddButtonClick });

    card.appendChild(image);
    card.appendChild(cardContent);
    cardContent.appendChild(title);
    cardContent.appendChild(variantContainer);
    cardContent.appendChild(cardBottom);
    cardBottom.appendChild(addButton);

    (async () => {
      const handle = variant.productHandle || variant.handle;
      let loaded = false;

      try {
        const upsellImage = await this._fetchUpsellProductImage(handle);
        if (upsellImage?.url) {
          image.src = upsellImage.url;
          image.alt = upsellImage.altText || selectedVariant.productTitle;
          loaded = true;
        }
      } catch (err) {
        console.error("Error fetching upsell metafield image", err);
      }

      if (!loaded) {
        image.src = selectedVariant.imageSrc || variant.productImageSrc;
        image.alt = selectedVariant.productTitle;
      }
    })();

    return card;
  }

  _handleUpsellAddButtonClick(evt, card, variant) {
    evt.preventDefault();

    const { totalQuantity } = this._getBundleTotals();
    const size = this._getSelectedBundleTotalSize();

    if (totalQuantity === 0) {
      this._showTemporaryAlert({
        text: "Please add bundle products first",
        duration: 3000,
      });
      return;
    }

    if (totalQuantity < size) {
      this._showTemporaryAlert({
        text: "Please complete your bundle selection first",
        duration: 3000,
      });
      return;
    }

    const selected = card.selectedVariant || variant.productVariants[0];
    const cardBottom = card.querySelector(".product-card__bottom");
    const existingSelector = cardBottom.querySelector(".quantity-selector");

    if (existingSelector) {
      cardBottom.removeChild(existingSelector);
    }

    const result = this._createUpsellQuantitySelector(card, { productVariants: [selected] }, 1);

    if (result && result.container instanceof HTMLElement) {
      const addButton = cardBottom.querySelector(".product-card__button");
      if (addButton && cardBottom.contains(addButton)) {
        cardBottom.replaceChild(result.container, addButton);
      } else {
        cardBottom.appendChild(result.container);
      }
    }
  }

  _createUpsellQuantitySelector(card, variant, initialQuantity = 0) {
    const selected = card.selectedVariant || variant.productVariants[0];
    const variantId = this._getVariantId(selected);

    const container = document.createElement("div");
    container.classList.add("quantity-selector");
    container.dataset.upsellVariantId = variantId;

    const minusBtn = document.createElement("button");
    minusBtn.classList.add("minus");
    minusBtn.type = "button";
    minusBtn.innerHTML = this._svgMinus;

    const display = document.createElement("span");
    display.classList.add("quantity-display");
    display.textContent = "0";

    const plusBtn = document.createElement("button");
    plusBtn.classList.add("plus");
    plusBtn.type = "button";
    plusBtn.innerHTML = this._svgPlus;

    container.append(minusBtn, display, plusBtn);

    const updateQty = (delta) => {
      let currentQty = parseInt(display.textContent) || 0;
      let newQty = currentQty + delta;

      if (newQty < 0) newQty = 0;

      const existingUpsell = this._upsellItems.find((u) => u.id === variantId);

      if (newQty === 0) {
        this._upsellItems = this._upsellItems.filter((u) => u.id !== variantId);
        display.textContent = "0";

        const cardBottom = card.querySelector(".product-card__bottom");
        if (cardBottom.contains(container)) {
          cardBottom.removeChild(container);
        }

        const addButton = card.querySelector(".product-card__button");
        if (!addButton) {
          const newAddButton = document.createElement("button");
          newAddButton.classList.add("product-card__button", "button", "button--destructive", "add-button");
          newAddButton.dataset.label = this._addToCartText;
          newAddButton.setAttribute("aria-label", this._addToCartText);
          newAddButton.innerHTML = this._addToCartText;

          const isReady = this._isBundleReady();
          newAddButton.classList.toggle("is-disabled", !isReady);

          const onAddButtonClick = (evt) => this._handleUpsellAddButtonClick(evt, card, variant);

          newAddButton.addEventListener("click", onAddButtonClick);
          this._listeners.push({ el: newAddButton, type: "click", fn: onAddButtonClick });

          cardBottom.appendChild(newAddButton);
        }
      } else {
        if (existingUpsell) {
          existingUpsell.quantity = newQty;
        } else {
          this._upsellItems.push({
            id: variantId,
            quantity: newQty,
            price: selected.price,
          });
        }
        display.textContent = newQty;
      }

      this._updateSubtotalSumBlock();
      this._updateStickySubtotalSumBlock();
    };

    const onPlus = () => updateQty(1);
    const onMinus = () => updateQty(-1);

    plusBtn.addEventListener("click", onPlus);
    minusBtn.addEventListener("click", onMinus);

    this._listeners.push({ el: plusBtn, type: "click", fn: onPlus });
    this._listeners.push({ el: minusBtn, type: "click", fn: onMinus });

    if (initialQuantity > 0) {
      updateQty(initialQuantity);
    }

    return { container, display };
  }

  _createUpsellVariantDropdown(variant, card, variantButton, cardBottom, image) {
    const dropdown = document.createElement("ul");
    dropdown.classList.add("product-card__dropdown");
    dropdown.style.display = "none";

    const onButtonClick = () => {
      document.querySelectorAll(".product-card__dropdown").forEach((drop) => {
        if (drop !== dropdown) drop.style.display = "none";
      });
      dropdown.style.display = dropdown.style.display === "none" ? "block" : "none";
    };
    variantButton.addEventListener("click", onButtonClick);
    this._listeners.push({ el: variantButton, type: "click", fn: onButtonClick });

    variant.productVariants.forEach((v) => {
      const item = document.createElement("li");
      item.textContent = v.productVariantTitle;

      const onItemClick = () => {
        card.selectedVariant = v;
        const variantId = v.platformId.split("/").pop();
        card.dataset.activeVariantId = variantId;
        variantButton.innerHTML = v.productVariantTitle;
        image.src = v.imageSrc || variant.productImageSrc;

        card.querySelector(".product-card__price").textContent = this._formatMoney(v.price);

        dropdown.style.display = "none";

        const oldSelector = cardBottom.querySelector(".quantity-selector");
        if (oldSelector) cardBottom.removeChild(oldSelector);
        const oldButton = cardBottom.querySelector(".product-card__button");
        if (oldButton) cardBottom.removeChild(oldButton);

        const addButton = document.createElement("button");
        addButton.classList.add("product-card__button", "button", "button--destructive");
        addButton.dataset.label = this._addToCartText;
        addButton.setAttribute("aria-label", this._addToCartText);
        addButton.innerHTML = this._addToCartText;
        cardBottom.appendChild(addButton);

        addButton.onclick = () => {
          const selected = card.selectedVariant || v;
          const { container } = this._createUpsellQuantitySelector(card, { productVariants: [selected] }, 1);
          container.dataset.upsellVariantId = this._getVariantId(selected);

          if (cardBottom.contains(addButton)) {
            cardBottom.replaceChild(container, addButton);
          } else {
            cardBottom.appendChild(container);
          }
        };
      };

      item.addEventListener("click", onItemClick);
      this._listeners.push({ el: item, type: "click", fn: onItemClick });

      dropdown.appendChild(item);
    });

    return dropdown;
  }

  _updateCardPrice(card) {
    const variantId = card.selectedVariant.platformId.split("/").pop();
    const priceData = this._getCurrentPriceData(variantId);
    const compareData = this._getComparePriceData(variantId);
    const compareEl = card.querySelector(".product-card__price-compare");

    compareEl.innerHTML = "";
    if (compareData.comparePrice > 0) {
      compareEl.textContent = `${this._formatMoney(compareData.comparePrice)}`;
    } else if (priceData.type === "selling_plan") {
      const originalPrice = this._getVariantPriceById(variantId, true);
      compareEl.textContent = `${this._formatMoney(originalPrice)}`;
    }
  }

  async _applyProductTagsToAllCards(variants, cards) {
    const root = this._elements?.container;
    const tagString = root?.dataset.tagString;
    if (!tagString) return;

    const targetTags = tagString
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

    const uniqueHandles = [...new Set(variants.map((v) => v.productHandle))];

    const requests = uniqueHandles.map((handle) =>
      fetch(`/products/${handle}.js`)
        .then((res) => res.json())
        .then((data) => ({
          handle,
          tags: Array.isArray(data.tags) ? data.tags.map((t) => t.toLowerCase()) : [],
        }))
        .catch((err) => {
          console.error("Ошибка получения тегов", handle, err);
          return { handle, tags: [] };
        }),
    );

    const results = await Promise.all(requests);
    const tagsMap = new Map(results.map((r) => [r.handle, r.tags]));

    variants.forEach((variant, index) => {
      const productTags = tagsMap.get(variant.productHandle) || [];
      const hasMatch = targetTags.some((tag) => productTags.includes(tag));

      if (hasMatch && cards[index]) {
        cards[index].classList.add("product-limited");
      }
    });
  }

  _cleanProductTitle(titleText) {
    if (!titleText) return "";

    if (titleText.includes("Variety Pack")) {
      titleText = titleText.replace("Shake", "").replace("Soup", "").replace("Oats", "");
      return titleText.split(" ").slice(1).join(" ").replaceAll("Variety Pack 10", "Variety 10 Sachets");
    }
    if (titleText.includes("Key Lime Pie")) {
      return "Key Lime Pie";
    }
    return titleText.split(" ").slice(2).join(" ").split(" - ")[0];
  }

  _buildVariantModeGrouping() {
    // Map each variant's Skio UUID -> { product, variant }; productVariantGrouping references these UUIDs.
    const byUuid = {};
    this._selectableProductVariants.forEach((product) => {
      (product.productVariants || []).forEach((variant) => {
        if (variant?.id) byUuid[variant.id] = { product, variant };
      });
    });

    // productVariantOrder is a list of numeric platformIds used to order cards within a group.
    const orderIndex = {};
    (this._productVariantOrder || []).forEach((platformId, index) => {
      orderIndex[String(platformId)] = index;
    });

    // Turn a single variant into a pseudo-product with exactly one entry in productVariants,
    // so _createProductCard renders it as an individual card (no variant dropdown).
    const makePseudoProduct = (product, variant) => {
      const variantTitle = variant.productVariantTitle;
      const isDefaultTitle = !variantTitle || variantTitle === "Default Title";
      const displayTitle = isDefaultTitle ? this._cleanProductTitle(product.productTitle) : variantTitle;

      return {
        productId: product.productId,
        productTitle: product.productTitle,
        productImageSrc: product.productImageSrc,
        productPlatformId: product.productPlatformId,
        productHandle: product.productHandle,
        displayTitle,
        productVariants: [variant],
      };
    };

    const sortByOrder = (pseudoProducts) =>
      pseudoProducts.sort((a, b) => {
        const aId = a.productVariants[0].platformId.split("/").pop();
        const bId = b.productVariants[0].platformId.split("/").pop();
        const aIndex = orderIndex[aId] ?? Number.MAX_SAFE_INTEGER;
        const bIndex = orderIndex[bId] ?? Number.MAX_SAFE_INTEGER;
        return aIndex - bIndex;
      });

    const grouped = {};
    const order = [];
    const usedUuids = new Set();

    (this._productVariantGrouping || []).forEach((group) => {
      const title = group.title;
      const pseudoProducts = (group.productVariantIds || [])
        .map((uuid) => byUuid[uuid])
        .filter(Boolean)
        .map(({ product, variant }) => {
          usedUuids.add(variant.id);
          return makePseudoProduct(product, variant);
        });

      if (pseudoProducts.length === 0) return;

      sortByOrder(pseudoProducts);

      if (!grouped[title]) {
        grouped[title] = [];
        order.push(title);
      }
      grouped[title].push(...pseudoProducts);
    });

    // Any variant not referenced by a group falls back to an "Other" group.
    const ungrouped = Object.values(byUuid)
      .filter(({ variant }) => !usedUuids.has(variant.id))
      .map(({ product, variant }) => makePseudoProduct(product, variant));

    if (ungrouped.length) {
      sortByOrder(ungrouped);
      grouped["Other"] = (grouped["Other"] || []).concat(ungrouped);
      if (!order.includes("Other")) order.push("Other");
    }

    this._groupedProducts = grouped;
    this._productGroupingOrder = order;
  }

  _renderProductCards() {
    if (!this._elements?.boxContainer) {
      console.error("Box container not found");
      return;
    }
    const container = document.createElement("div");
    container.classList.add("skio-bundle-builder__product-container");

    const subscriptionUpsellContainer = this._elements.subscriptionUpsellsContainer;

    const allVariants = [];
    const allCards = [];

    if (this._groupedProducts && typeof this._groupedProducts === "object") {
      const orderedCategories = this._productGroupingOrder || [];

      if (!orderedCategories || orderedCategories.length === 0) {
        console.error("No categories to render");
        return;
      }

      orderedCategories.forEach((category) => {
        const variants = this._groupedProducts?.[category];
        if (!variants || variants.length === 0) {
          console.warn("Skipping category because no variants:", category);
          return;
        }

        const categoryBlock = document.createElement("div");
        categoryBlock.classList.add("skio-bundle-builder__product-category-block");
        categoryBlock.dataset.category = category;

        const heading = document.createElement("h2");
        heading.classList.add("skio-bundle-builder__product-category-title", "poppins-heading");

        const headingText = document.createElement("span");
        headingText.textContent = category;

        const productList = document.createElement("div");
        productList.classList.add("skio-bundle-builder__product-list");

        variants.forEach((variant) => {
          const card = this._createProductCard(variant);
          productList.appendChild(card);

          allVariants.push(variant);
          allCards.push(card);
        });

        heading.appendChild(headingText);
        categoryBlock.appendChild(heading);
        categoryBlock.appendChild(productList);

        container.appendChild(categoryBlock);
      });
    } else {
      console.error("Unexpected data structure:", this._groupedProducts);
    }

    this._elements.boxContainer.appendChild(container);

    if (allVariants.length > 0) {
      this._applyProductTagsToAllCards(allVariants, allCards);
    }
  }

  _renderProductUpsells() {
    if (!this._elements?.upsellsContainer) return;
    const upsellsContainer = document.createElement("div");
    upsellsContainer.classList.add("skio-bundle-builder__product-container");

    const upsellsVariants = this._oneTimeUpsells;
    if (!upsellsVariants || upsellsVariants.length === 0) {
      return;
    }

    const upsellsProductList = document.createElement("div");
    upsellsProductList.classList.add("skio-bundle-builder__product-list");

    upsellsVariants.forEach((variant) => {
      const upsellsCard = this._createUpsellCard(variant);

      upsellsProductList.appendChild(upsellsCard);
      this._elements.upsellsContainer.appendChild(upsellsProductList);
    });
  }

  _getVariantId(selectedVariant) {
    return selectedVariant.platformId.split("/").pop();
  }

  _getVariantPrice(selectedVariant) {
    return selectedVariant.price;
  }

  _getProductByVariantId(variantId) {
    return this._selectableProductVariants.find((product) =>
      product.productVariants.some((variant) => {
        const variantNum = variant.platformId.split("/").pop();
        return variantNum === variantId;
      }),
    );
  }

  _resetCardToAddButton(card, addButton) {
    const cardBottom = card.querySelector(".product-card__bottom");

    if (!cardBottom.contains(addButton)) {
      cardBottom.appendChild(addButton);
    }

    const selector = cardBottom.querySelector(".quantity-selector");
    if (selector) cardBottom.removeChild(selector);
  }

  _resetCardUIAfterRemove(variantId) {
    const card = document.querySelector(`.product-card[data-variant-id="${variantId}"]`);
    if (!card) return;

    const addButton = card.querySelector("[data-add-to-bundle]");
    if (!addButton) return;

    this._resetCardToAddButton(card, addButton);
  }

  _createBundleQuantitySelector(card, variant, initialQuantity = 0) {
    const selected = card.selectedVariant || variant.productVariants[0];
    const variantId = this._getVariantId(selected);

    const cardBottom = card.querySelector(".product-card__bottom");

    const container = document.createElement("div");
    container.classList.add("quantity-selector");

    const minusBtn = document.createElement("button");
    minusBtn.classList.add("minus");
    minusBtn.type = "button";
    minusBtn.innerHTML = this._svgMinus;

    const display = document.createElement("span");
    display.classList.add("quantity-display");
    display.textContent = "0";

    const plusBtn = document.createElement("button");
    plusBtn.classList.add("plus");
    plusBtn.type = "button";
    plusBtn.innerHTML = this._svgPlus;

    container.append(minusBtn, display, plusBtn);

    const updateQty = (delta) => {
      this._addToBundle(selected, delta);

      const item = this._bundleItems.find((i) => i.id === variantId);
      display.textContent = item ? item.quantity : "0";

      if (!item || item.quantity <= 0) {
        this._removeFromBundleById(variantId);

        if (cardBottom.contains(container)) {
          cardBottom.removeChild(container);
        }

        this._recreateAddButton(card, cardBottom, variant);
      }
    };

    const onPlus = () => updateQty(1);
    const onMinus = () => updateQty(-1);

    plusBtn.addEventListener("click", onPlus);
    minusBtn.addEventListener("click", onMinus);

    this._listeners.push({ el: plusBtn, type: "click", fn: onPlus });
    this._listeners.push({ el: minusBtn, type: "click", fn: onMinus });

    if (initialQuantity > 0) {
      updateQty(initialQuantity);
    }

    return { container, display };
  }

  _recreateAddButton(card, cardBottom, variant) {
    const oldButton = cardBottom.querySelector(".product-card__button");
    if (oldButton) {
      cardBottom.removeChild(oldButton);
    }

    const addButton = document.createElement("button");
    addButton.classList.add("product-card__button", "button", "button--destructive", "add-button");
    addButton.dataset.label = this._addToCartText;
    addButton.setAttribute("aria-label", this._addToCartText);
    addButton.innerHTML = this._addToCartText;
    addButton.dataset.inCart = "false";

    const onAddButtonClick = (evt) => this._handleAddButtonClick(evt, card, variant || card._variantData);
    addButton.addEventListener("click", onAddButtonClick);
    this._listeners.push({ el: addButton, type: "click", fn: onAddButtonClick });

    cardBottom.appendChild(addButton);
  }

  _updateBundleControls(maxSize) {
    const { totalQuantity } = this._getBundleTotals();

    document.querySelectorAll(".product-card:not(.product-card--upsell) .add-button").forEach((btn) => {
      btn.disabled = totalQuantity >= maxSize;
      btn.classList.toggle("disabled", totalQuantity >= maxSize);
    });

    document
      .querySelectorAll(".product-card:not(.product-card--upsell) .quantity-selector .plus")
      .forEach((plusBtn) => {
        plusBtn.disabled = totalQuantity >= maxSize;
        plusBtn.classList.toggle("disabled", totalQuantity >= maxSize);
      });
  }

  _getBundleTotals() {
    let totalQuantity = 0;
    let totalOriginalPrice = 0;

    const selectedSizeInput = document.querySelector('input[name="bundle-size"]:checked');
    const selectedSize = selectedSizeInput ? parseInt(selectedSizeInput.value) : 1;

    this._bundleItems.forEach((item) => {
      const quantity = parseInt(item.quantity || 1);
      totalQuantity += quantity;

      const original = item.originalPrice != null ? item.originalPrice : item.price;
      totalOriginalPrice += original * quantity;
    });

    // The double option moves the box onto the larger sizeInterval's tier.
    const percent = this._getDiscountPercent(
      this._getEffectivePricingSize() || selectedSize,
      this._isSellingPlanSelected(),
    );

    let totalPrice = 0;

    this._bundleItems.forEach((item) => {
      const quantity = parseInt(item.quantity || 1);
      const isSubscription = item.isSubscription === true;

      let unitPrice;
      let finalUnitPrice;

      if (isSubscription) {
        const basePrice = item.adjustedPrice && item.adjustedPrice > 0 ? item.adjustedPrice : item.price;

        finalUnitPrice = this._roundMoney(basePrice);
      } else {
        unitPrice = this._roundMoney(item.price);
        const rawUnitDiscount = unitPrice * (percent / 100);
        const unitDiscount = this._floorMoney(rawUnitDiscount);
        finalUnitPrice = unitPrice - unitDiscount;
      }

      totalPrice += finalUnitPrice * quantity;
    });

    // A double subscription buys more of each pick. totalQuantity stays as-is because it drives the
    // "how full is the box" progress/limit logic, but every money figure scales with the multiplier.
    const quantityMultiplier = this._getPurchaseQuantityMultiplier();
    totalPrice *= quantityMultiplier;
    totalOriginalPrice *= quantityMultiplier;

    const totalSavings = Math.max(0, totalOriginalPrice - totalPrice);

    return { totalQuantity, totalPrice, totalOriginalPrice, totalSavings };
  }

  _getBundleSelectionStatus() {
    const maxSize = this._getSelectedBundleTotalSize();
    const { totalQuantity } = this._getBundleTotals();

    const selectedQuantity = totalQuantity;
    const remainingQuantity = Math.max(maxSize - totalQuantity, 0);

    return { selectedQuantity, remainingQuantity, maxQuantity: maxSize };
  }

  _getUpsellTotals() {
    let upsellTotal = 0;

    this._upsellItems.forEach((item) => {
      const quantity = parseInt(item.quantity || 1);
      upsellTotal += item.price * quantity;
    });

    return upsellTotal;
  }

  _showTemporaryAlert({ duration = 3000, text } = {}) {
    if (this._elements.alertContainer) {
      this._elements.alertContainer.innerHTML = text;
      this._elements.alertContainer.classList.remove("custom-hidden");

      clearTimeout(this._removeAlert);
      this._removeAlert = setTimeout(() => {
        this._elements.alertContainer.innerHTML = "";
        this._elements.alertContainer.classList.add("custom-hidden");
      }, duration);
    }
  }

  _renderProgressBarBlocks() {
    const progressBar = this._elements.progressBar;
    if (!progressBar) return;

    const maxQuantity = this._getSelectedBundleTotalSize();

    progressBar.innerHTML = "";

    for (let i = 0; i < maxQuantity; i++) {
      const block = document.createElement("div");
      block.classList.add("progress-block");
      block.dataset.index = i;
      progressBar.appendChild(block);
    }

    this._updateProgressBarBlocks();
  }

  _updateProgressBarBlocks() {
    const progressBar = this._elements.progressBar;
    const progressBarText = this._elements.progressBarText;

    if (!progressBar || !progressBarText) return;

    progressBarText.innerHTML = "";

    const { remainingQuantity, maxQuantity } = this._getBundleSelectionStatus();
    const usedQuantity = maxQuantity - remainingQuantity;

    if (remainingQuantity === 0) {
      progressBarText.innerHTML = `<strong>Your bundle</strong> — ${maxQuantity} items`;
    } else {
      progressBarText.innerHTML = `Select <span data-progress-quantity>${remainingQuantity}</span> more items`;
    }

    const blocks = progressBar.querySelectorAll(".progress-block");

    const icons = [];
    this._bundleItems.forEach((item) => {
      for (let i = 0; i < item.quantity; i++) {
        icons.push(item.icon || null);
      }
    });

    blocks.forEach((block, index) => {
      if (index < usedQuantity) {
        block.classList.add("filled");

        const icon = icons[index];
        if (icon) {
          block.style.backgroundImage = `url(${icon})`;
          block.style.backgroundSize = "cover";
          block.style.backgroundPosition = "center";
        } else {
          block.style.backgroundImage = "";
        }
      } else {
        block.classList.remove("filled");
        block.style.backgroundImage = "";
      }
    });
  }

  _updateRemainingQuantityBlock() {
    if (!this._elements?.remainQuantityBlock || !this._elements?.remainQuantityContainer) return;

    const { remainingQuantity, maxQuantity } = this._getBundleSelectionStatus();
    const usedQuantity = maxQuantity - remainingQuantity;

    this._elements.remainQuantityBlock.textContent = `${remainingQuantity}`;

    if (remainingQuantity === 0) {
      this._elements.remainQuantityContainer.classList.add("custom-hidden");
    } else {
      this._elements.remainQuantityContainer.classList.remove("custom-hidden");
    }

    this._updateProgressBarBlocks();
    this._updateStickyUI();
  }

  _updateSubtotalSumBlock() {
    if (!this._elements.totalsContentBlock && !this._elements?.subtotalSumBlock) return;

    const { totalPrice, totalOriginalPrice } = this._getBundleTotals();
    const upsellTotal = this._getUpsellTotals();
    const bundleTotalSize = this._getSelectedBundleTotalSize();
    const { totalQuantity } = this._getBundleTotals();
    const selectedQuantity = totalQuantity;
    const isSubscriptionSelected = this._isSellingPlanSelected();
    const compareTotalPrice =
      totalOriginalPrice +
      upsellTotal +
      (isSubscriptionSelected ? this._getSubscriptionAddedCompare(this._getPurchaseQuantityMultiplier()) : 0);
    const finalTotalPrice = totalPrice + upsellTotal;
    const mealQuantity = this._mealQuantity;
    // totalPrice is already doubled for a double subscription, so the meal count has to be too.
    const mealTotal = selectedQuantity * mealQuantity * this._getPurchaseQuantityMultiplier();
    const mealPrice = this._formatMoney(totalPrice / mealTotal);

    this._elements.subtotalCompareSumBlock.textContent = `${this._formatMoney(compareTotalPrice)}`;
    this._elements.subtotalSumBlock.textContent = `${this._formatMoney(finalTotalPrice)}`;
    this._elements.subtotalMealPriceBlock.innerHTML = `<span class="skio-bundle-builder__cost-per-meal">${mealPrice}</span> per Meal`;

    const saveBlockWrapper = this._elements.totalsContentBlock;

    if (selectedQuantity >= bundleTotalSize) {
      saveBlockWrapper.classList.remove("custom-hidden");
    } else {
      saveBlockWrapper.classList.add("custom-hidden");
    }

    this._updatePaymentBlock();
  }

  _toggleCheckoutButton() {
    if (!this._elements?.checkoutButton || !this._elements?.checkoutBlock) return;

    const remainingQuantity = parseInt(this._elements.remainQuantityBlock.textContent, 10);

    if (remainingQuantity === 0) {
      this._elements.checkoutBlock.classList.remove("custom-hidden");
      this._elements.checkoutButton.classList.remove("custom-hidden");
      const subscriptionFrequencySection = document.getElementById("skio-select-subscription-frequency");
      /* if (subscriptionFrequencySection) {
        subscriptionFrequencySection.scrollIntoView({ behavior: "smooth", block: "center" });
      } */
    } else {
      this._elements.checkoutBlock.classList.add("custom-hidden");
      this._elements.checkoutButton.classList.add("custom-hidden");
    }
  }

  _updateBundleItemsForPurchaseOption() {
    this._bundleItems = this._bundleItems.map((item) => {
      const priceData = this._getCurrentPriceData(item.id);
      return {
        ...item,
        price: priceData.price,
        planId: priceData.planId,
      };
    });

    const maxSize = this._getSelectedBundleTotalSize();
    this._updateBundleControls(maxSize);
    this._updateUpsellButtonsState();
    this._updateRemainingQuantityBlock();
    this._updateSubtotalSumBlock();
    this._toggleCheckoutButton();
  }

  async _fetchVariantIconImage(variantId) {
    const query = `
      query ($id: ID!) {
        node(id: $id) {
          ... on ProductVariant {
            metafield(namespace: "custom", key: "variant_image") {
              reference {
                ... on MediaImage {
                  image {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch("/api/2024-01/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": "ae3098bf902396fd7f396049b17d60fb",
        },
        body: JSON.stringify({
          query,
          variables: { id: variantId },
        }),
      });

      const json = await response.json();
      return json?.data?.node?.metafield?.reference?.image || null;
    } catch (error) {
      console.error("Error fetching variant icon image:", error);
      return null;
    }
  }

  _addToBundle(variant, qty = 1) {
    const maxSize = this._getSelectedBundleTotalSize();
    const { totalQuantity } = this._getBundleTotals();

    if (totalQuantity + qty > maxSize) {
      this._updateBundleControls(maxSize);
      this._updateUpsellButtonsState();
      this._updateRemainingQuantityBlock();
      this._updateSubtotalSumBlock();
      this._toggleCheckoutButton();
      return;
    }

    const variantId = this._getVariantId(variant);
    const priceData = this._getCurrentPriceData(variantId);

    const existing = this._bundleItems.find((i) => i.id === variantId);

    if (existing) {
      existing.quantity += qty;

      if (existing.quantity <= 0) {
        this._bundleItems = this._bundleItems.filter((i) => i.id !== variantId);
      } else {
        existing.price = priceData.price;
        existing.planId = priceData.planId;
        if (!existing.originalPrice) {
          existing.originalPrice = this._getVariantPrice(variant, true);
        }
      }
    } else if (qty > 0) {
      const product = this._getProductByVariantId(variantId);

      const newItem = {
        product,
        id: variantId,
        quantity: qty,
        price: priceData.price,
        originalPrice: this._getVariantPrice(variant, true),
        planId: priceData.planId,
        icon: null,
      };

      this._bundleItems.push(newItem);

      const self = this;

      const variantGID = variant.platformId;
      this._fetchVariantIconImage(variantGID)
        .then((iconImage) => {
          const imageUrl = iconImage?.url || null;
          if (imageUrl) {
            newItem.icon = imageUrl;
            self._updateProgressBarBlocks();
            self._updateStickyProgressBarBlocks();
          }
        })
        .catch((err) => console.error("Error fetching custom.variant_image", err));
    }

    this._updateBundleControls(maxSize);
    this._updateUpsellButtonsState();
    this._updateRemainingQuantityBlock();
    this._updateSubtotalSumBlock();
    this._toggleCheckoutButton();
  }

  _removeFromBundle(variant, qty = 1) {
    const variantId = this._getVariantId(variant);
    const existing = this._bundleItems.find((i) => i.id === variantId);
    if (!existing) return;
    existing.quantity -= qty;
    if (existing.quantity <= 0) this._bundleItems = this._bundleItems.filter((i) => i.id !== variantId);
  }

  _getBundleItemQty(variantId) {
    const existing = this._bundleItems.find((i) => i.id === variantId);
    return existing ? existing.quantity : 0;
  }

  _resetBundleUI() {
    document.querySelectorAll(".add-button").forEach((btn) => {
      btn.classList.remove("disabled");
      btn.disabled = false;
    });

    const allCards = document.querySelectorAll(".product-card");
    allCards.forEach((card) => {
      const variant = card._variantData;
      this._resetBundleSelector(card, variant);
    });

    this._updateSubtotalSumBlock();

    this._bundleItems = [];
    this._updateRemainingQuantityBlock();
    this._toggleCheckoutButton();
  }

  _removeFromBundleById(variantId) {
    const allVariants = this._getAllVariants(this._selectableProductVariants);
    const variant = allVariants.find((v) => v.platformId.split("/").pop() === String(variantId));
    const maxSize = this._getMaxNumber(this._sizeInterval);

    if (variant) {
      const card = document.querySelector(`.product-card[data-active-variant-id="${variantId}"]`);

      const existingItem = this._bundleItems.find((i) => i.id === variantId);
      if (existingItem) {
        this._removeFromBundle(variant, existingItem.quantity);
      }

      if (card) {
        this._resetBundleSelector(card, variant);
      }

      this._updateSubtotalSumBlock();
      this._updateRemainingQuantityBlock();
      this._toggleCheckoutButton();
      this._updateBundleControls(maxSize);
      this._updateUpsellButtonsState();
    }
  }

  _trackBundleAddToCart(items, boxIndex) {
    const size = this._getEffectivePricingSize() || this._getSelectedBundleTotalSize();
    const { totalPrice } = this._getBundleTotals();
    const selectedRadio = document.querySelector('input[name="purchase-option"]:checked');
    const isSubscription = selectedRadio?.dataset.radioType === "selling_plan";
    const isDoubleSubscription = this._isDoubleSubscriptionSelected();
    const quantityMultiplier = this._getPurchaseQuantityMultiplier();
    const sellingPlanId = isSubscription ? selectedRadio?.value : null;

    const allVariants = this._getAllVariants(this._selectableProductVariants);
    const gaItems = items.map((item) => {
      const variant = allVariants.find((v) => v.platformId?.split("/").pop() === String(item.id));
      return {
        item_id: String(item.id),
        item_name: item.product?.productTitle || String(item.id),
        item_variant: variant?.productVariantTitle || "",
        quantity: (parseInt(item.quantity) || 1) * quantityMultiplier,
        price: parseFloat((item.price || 0).toFixed(2)),
        item_category: "Bundle",
      };
    });

    // GA4
    if (window.gtag) {
      window.gtag("event", "add_to_cart", {
        currency: this._userCurrencyCode || this._boxCurrencyCode || "AUD",
        value: totalPrice,
        items: gaItems,
      });

      window.gtag("event", "bundle_add_to_cart", {
        currency: this._userCurrencyCode || this._boxCurrencyCode || "AUD",
        value: totalPrice,
        bundle_size: size,
        bundle_id: this._boxId,
        bundle_index: boxIndex,
        purchase_type: isDoubleSubscription ? "double_subscription" : isSubscription ? "subscription" : "one_time",
        selling_plan_id: sellingPlanId,
        items: gaItems,
      });
    }

    // Intelligems
    if (window.ig) {
      window.ig("trackEvent", "bundle_add_to_cart", {
        value: totalPrice,
        bundle_size: size,
        bundle_id: this._boxId,
        purchase_type: isDoubleSubscription ? "double_subscription" : isSubscription ? "subscription" : "one_time",
      });
    }
  }

  async _addBundleToCart() {
    if (!this._bundleItems.length) return;

    const checkoutBtn = document.querySelector(".skio-bundle-builder__checkout");
    const buttonText = checkoutBtn?.querySelector("span");

    const checkoutStickyBtn = document.querySelector(".skio-bundle-builder__sticky-checkout");
    const stickyButtonText = checkoutStickyBtn?.querySelector("span");

    [checkoutBtn, checkoutStickyBtn].forEach((btn) => {
      if (btn) btn.disabled = true;
    });
    if (buttonText) buttonText.textContent = "Adding...";
    if (stickyButtonText) stickyButtonText.textContent = "Adding...";

    let boxIndex = 0;
    try {
      const cartResponse = await fetch("/cart.js");
      const cartData = await cartResponse.json();
      const existingBoxIndexes = cartData.items
        .map((cartItem) => Number(cartItem.properties?._dynamicBoxIndex))
        .filter((index) => !isNaN(index));

      if (existingBoxIndexes.length > 0) {
        boxIndex = Math.max(...existingBoxIndexes) + 1;
      }
    } catch (err) {
      console.error("Error:", err);
    }

    const itemsToAdd = [...this._bundleItems];
    const upsellItems = this._upsellItems.map((item) => ({
      id: Number(item.id),
      quantity: Number(item.quantity),
      properties: {
        _dynamicBoxIndex: boxIndex,
        _is_upsell: "true",
      },
    }));

    // A double subscription buys the picks at the next sizeInterval up, so each line scales by that
    // size ratio and the box is tagged with the size it actually adds up to.
    const quantityMultiplier = this._getPurchaseQuantityMultiplier();
    const cartBundleSize = this._getEffectivePricingSize() || this._getSelectedBundleTotalSize();

    const allItems = [
      ...itemsToAdd.map((item) => {
        const body = {
          id: Number(item.id),
          quantity: Number(item.quantity) * quantityMultiplier,
          properties: {
            _dynamicBoxIndex: boxIndex,
            _dynamicBoxId: this._boxId,
            _bundle_size: String(cartBundleSize) + "_bags",
            // How many boxes' worth this is, and how many items make up one box, so the cart gift
            // watcher can re-derive the gift allowance as the customer edits quantities.
            _gift_multiplier: String(quantityMultiplier),
            _gift_step: String(parseInt(this._getSelectedBundleTotalSize()) || 0),
            // Which gifts this box offers, so the watcher stops relying on a theme-wide list and
            // adds nothing on a builder whose special offer blocks are turned off. "none" rather
            // than blank because Shopify drops empty line item properties.
            _gift_ids: this._giftVariantIds.join(",") || "none",
            _is_bundle: "true",
          },
        };
        if (item.planId) body.selling_plan = Number(item.planId);
        return body;
      }),
      ...upsellItems,
    ];

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ items: allItems }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error:", errorText);
        throw new Error(errorText);
      }

      this._bundleItems = [];
      this._upsellItems = [];

      this._trackBundleAddToCart(itemsToAdd, boxIndex);

      const selectedPurchaseRadio = document.querySelector('input[name="purchase-option"]:checked');
      const isSubscriptionPurchase = selectedPurchaseRadio?.dataset.radioType === "selling_plan";

      window._bundleGiftAddInProgress = isSubscriptionPurchase && this._giftVariantIds.length > 0;

      const giftCartPromise = window._bundleGiftAddInProgress
        ? fetch("/cart.js")
            .then((r) => r.json())
            .catch((err) => {
              console.error("Error fetching cart for gift check:", err);
              return null;
            })
        : null;

      if (window._bundleGiftAddInProgress && giftCartPromise) {
        const cart = await giftCartPromise;
        await this._addGiftsToCart(boxIndex, cart);
      }

      window._bundleGiftAddInProgress = false;

      [checkoutBtn, checkoutStickyBtn].forEach((btn) => {
        if (btn) btn.disabled = false;
      });
      if (buttonText) buttonText.textContent = "Add to cart";
      if (stickyButtonText) stickyButtonText.textContent = "Add to cart";

      this._resetBundleUI();
      this._resetUpsellUI();

      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        await cartDrawer.updateContentsAndOpen();
        cartDrawer.open();
        this._lockGiftItemsInDrawer();
      }

      setTimeout(() => this._lockGiftItemsInDrawer(), 300);
    } catch (err) {
      console.error("Error:", err);
      [checkoutBtn, checkoutStickyBtn].forEach((btn) => {
        if (btn) btn.disabled = false;
      });
      if (buttonText) buttonText.textContent = "Add to cart";
      if (stickyButtonText) stickyButtonText.textContent = "Add to cart";
    }
  }

  _resetUpsellUI() {
    document.querySelectorAll(".product-card--upsell").forEach((card) => {
      const cardBottom = card.querySelector(".product-card__bottom");
      const selector = cardBottom?.querySelector(".quantity-selector");

      if (selector) {
        cardBottom.removeChild(selector);
      }

      const existingButton = cardBottom?.querySelector(".add-button");
      if (existingButton) return;

      const variant = card._variantData;
      const addButton = document.createElement("button");
      addButton.classList.add("product-card__button", "button", "button--destructive", "add-button");
      addButton.dataset.label = this._addToCartText;
      addButton.setAttribute("aria-label", this._addToCartText);
      addButton.innerHTML = this._addToCartText;

      const isReady = this._isBundleReady();
      addButton.disabled = !isReady;
      addButton.classList.toggle("is-disabled", !isReady);

      const onAddButtonClick = (evt) => this._handleUpsellAddButtonClick(evt, card, variant);
      addButton.addEventListener("click", onAddButtonClick);
      this._listeners.push({ el: addButton, type: "click", fn: onAddButtonClick });

      cardBottom?.appendChild(addButton);
    });
  }

  async _addGiftsToCart(boxIndex, cart = null) {
    if (!this._giftVariantIds.length) return;

    const existingGiftQuantities = new Map();
    try {
      if (!cart) {
        const cartRes = await fetch("/cart.js");
        cart = await cartRes.json();
      }

      cart.items
        .filter((i) => i.properties?._is_gift === "true" && Number(i.properties?._dynamicBoxIndex) === Number(boxIndex))
        .forEach((i) => {
          const variantId = String(i.variant_id);
          existingGiftQuantities.set(variantId, (existingGiftQuantities.get(variantId) || 0) + i.quantity);
        });
    } catch (err) {
      console.error("Error checking cart for existing gifts:", err);
    }

    // A double subscription is that many boxes' worth, so the free gifts scale with it.
    const giftQuantity = this._getPurchaseQuantityMultiplier();
    const giftBundleSize = this._getEffectivePricingSize() || this._getSelectedBundleTotalSize();

    const items = this._giftVariantIds
      .map((id) => {
        // /cart/add.js adds to whatever is already there, so only top up the shortfall.
        const missingQuantity = giftQuantity - (existingGiftQuantities.get(String(id)) || 0);
        if (missingQuantity <= 0) return null;

        return {
          id: Number(id),
          quantity: missingQuantity,
          properties: {
            _dynamicBoxIndex: boxIndex,
            _is_gift: "true",
            _bundle_size: String(giftBundleSize) + "_bags",
          },
        };
      })
      .filter(Boolean);

    if (!items.length) return;

    try {
      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
    } catch (err) {
      console.error("Error adding gifts:", err);
    }
  }

  async _removeGiftsFromCart(boxIndex) {
    try {
      const res = await fetch("/cart.js");
      const cart = await res.json();
      const updates = {};
      cart.items.forEach((item) => {
        const isSameBox = Number(item.properties?._dynamicBoxIndex) === boxIndex;
        const isGift = item.properties?._is_gift === "true";
        if (isSameBox && isGift) {
          updates[item.key] = 0;
        }
      });
      if (!Object.keys(updates).length) return;
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });
    } catch (err) {
      console.error("Error removing gifts:", err);
    }
  }

  _initCartGiftWatcher() {
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      let lockScheduled = false;
      this._cartGiftObserver = new MutationObserver(() => {
        if (lockScheduled) return;
        lockScheduled = true;
        setTimeout(() => {
          this._lockGiftItemsInDrawer();
          lockScheduled = false;
        }, 100);
      });
      this._cartGiftObserver.observe(cartDrawer, { childList: true, subtree: true });
    }
  }

  _lockGiftItemsInDrawer() {
    if (!this._giftVariantIds.length) return;

    const rows = document.querySelectorAll("tr.cart-item");

    rows.forEach((row) => {
      const input = row.querySelector("input.quantity__input");
      if (!input) return;

      const variantId = String(input.dataset.quantityVariantId || "");
      const isGift = this._giftVariantIds.some((id) => String(id) === variantId);

      if (!isGift) return;

      const priceEl = row.querySelector(".price--end");
      const isFree = priceEl?.textContent?.includes("$0.00") || priceEl?.textContent?.trim() === "$0.00";

      if (!isFree) return;

      row.classList.add("cart-item--gift");

      row.querySelectorAll(".quantity__button").forEach((btn) => {
        btn.style.pointerEvents = "none";
      });

      input.disabled = true;
      input.style.pointerEvents = "none";
    });
  }

  _setupCheckoutButtonListener() {
    const checkoutBtn = this._elements.checkoutButton;

    if (!checkoutBtn) return;

    const onCheckoutClick = async (e) => {
      e.preventDefault();
      await this._addBundleToCart();
    };
    checkoutBtn.addEventListener("click", onCheckoutClick);
    this._listeners.push({ el: checkoutBtn, type: "click", fn: onCheckoutClick });
  }

  _getProgressData() {
    const { totalQuantity } = this._getBundleTotals();
    const size = Number(this._getSelectedBundleTotalSize());

    const progress = size > 0 ? Math.min((totalQuantity / size) * 100, 100) : 0;

    return { totalQuantity, size, progress };
  }

  _renderStickyProgressBarBlocks() {
    const progressBar = this._sticky?.progressBar;
    if (!progressBar) return;

    const maxQuantity = this._getSelectedBundleTotalSize();
    progressBar.innerHTML = "";

    for (let i = 0; i < maxQuantity; i++) {
      const block = document.createElement("div");
      block.classList.add("progress-block");
      block.dataset.index = i;
      progressBar.appendChild(block);
    }

    this._updateStickyProgressBarBlocks();
  }

  _updateStickyProgressBarBlocks() {
    const progressBar = this._sticky?.progressBar;
    const progressBarText = this._sticky?.progressBarText;

    if (!progressBar || !progressBarText) return;

    progressBarText.innerHTML = "";

    const { remainingQuantity, maxQuantity } = this._getBundleSelectionStatus();
    const usedQuantity = maxQuantity - remainingQuantity;

    if (remainingQuantity === 0) {
      progressBarText.innerHTML = `<strong>Your bundle</strong> — ${maxQuantity} items`;
    } else {
      progressBarText.innerHTML = `Select <span data-progress-quantity>${remainingQuantity}</span> more items`;
    }

    const blocks = progressBar.querySelectorAll(".progress-block");

    const icons = [];
    this._bundleItems.forEach((item) => {
      for (let i = 0; i < item.quantity; i++) {
        icons.push(item.icon || null);
      }
    });

    blocks.forEach((block, index) => {
      if (index < usedQuantity) {
        block.classList.add("filled");

        const icon = icons[index];
        if (icon) {
          block.style.backgroundImage = `url(${icon})`;
          block.style.backgroundSize = "cover";
          block.style.backgroundPosition = "center";
        } else {
          block.style.backgroundImage = "";
        }
      } else {
        block.classList.remove("filled");
        block.style.backgroundImage = "";
      }
    });

    this._toggleStickyCheckoutButton(remainingQuantity);
  }

  _updateStickyNoteAndButton() {
    if (!this._sticky?.container) return;

    const isReady = this._isBundleReady();

    if (this._sticky.note) {
      this._sticky.note.classList.toggle("is-complete", isReady);
    }

    if (this._sticky.checkoutButton) {
      this._sticky.checkoutButton.classList.toggle("custom-hidden", !isReady);
    }
  }

  _toggleStickyCheckoutButton(remainingQuantity) {
    const button = this._sticky?.checkoutButton;
    const note = this._sticky?.noteBlock;
    if (!button && !note) return;

    if (remainingQuantity === 0) {
      button.classList.remove("custom-hidden");
      note?.classList.add("custom-hidden");
    } else {
      button.classList.add("custom-hidden");
      note?.classList.remove("custom-hidden");
    }
  }

  _updateStickyRemainingQuantityBlock() {
    const el = this._sticky;
    if (!el?.container || !el?.quantity) return;

    const { remainingQuantity, maxQuantity } = this._getBundleSelectionStatus();

    const usedQuantity = maxQuantity - remainingQuantity;

    el.quantity.textContent = `${remainingQuantity}`;

    if (el.fill && maxQuantity > 0) {
      const progress = (usedQuantity / maxQuantity) * 100;
      el.fill.style.width = `${progress}%`;
    }
  }

  _bindStickyCheckoutButton() {
    const stickyBtn = this._sticky?.checkoutButton;
    const mainBtn = this._elements?.checkoutButton;

    if (!stickyBtn || !mainBtn) return;

    const onStickyBtnClick = (e) => {
      e.preventDefault();
      mainBtn.click();
    };

    stickyBtn.addEventListener("click", onStickyBtnClick);
    this._listeners.push({ el: stickyBtn, type: "click", fn: onStickyBtnClick });
  }

  _updateStickySubtotalSumBlock() {
    const el = this._sticky;
    if (!el?.totalsWrapper || !el?.subtotal || !el?.compare || !el?.meal) return;

    const bundleTotalSize = this._getSelectedBundleTotalSize();

    const { totalQuantity, totalPrice } = this._getBundleTotals();
    const selectedQuantity = totalQuantity;

    const cheapestVariant = this._getCheapestSelectableVariant();
    const comparePrice = cheapestVariant.price;

    const upsellTotal = this._getUpsellTotals();
    const finalTotalPrice = totalPrice + upsellTotal;

    const quantityMultiplier = this._getPurchaseQuantityMultiplier();
    const compareTotalPrice =
      comparePrice * selectedQuantity * quantityMultiplier +
      upsellTotal +
      (this._isSellingPlanSelected() ? this._getSubscriptionAddedCompare(quantityMultiplier) : 0);

    const mealQuantity = this._mealQuantity || 1;
    // totalPrice already covers the doubled quantities, so the meal count has to as well.
    const mealTotal = selectedQuantity * mealQuantity * quantityMultiplier;

    if (mealTotal > 0) {
      const mealPrice = this._formatMoney(totalPrice / mealTotal);
      el.meal.innerHTML = `<span class="skio-bundle-builder__cost-per-meal">${mealPrice}</span> per ${this._mealTypeSingular().toLowerCase()}`;
    }

    el.compare.textContent = compareTotalPrice > finalTotalPrice ? this._formatMoney(compareTotalPrice) : "";

    el.subtotal.textContent = this._formatMoney(finalTotalPrice);

    el.totalsWrapper.classList.toggle("custom-hidden", selectedQuantity < bundleTotalSize);
  }

  _updateStickyUI() {
    this._updateStickyRemainingQuantityBlock();
    this._updateStickyProgressBarBlocks();
    this._updateStickySubtotalSumBlock();
  }

  _updateMediaBySize(shouldScroll = false) {
    const selectedSize = String(this._getSelectedBundleTotalSize());

    const sizes = this._sizeInterval.map(String);

    const mediaItems = document.querySelectorAll(".product__media-item, .thumbnail-list__item");
    let firstVisibleMedia = null;

    mediaItems.forEach((el) => {
      const img = el.querySelector("img");
      if (!img) return;

      const alt = (img.getAttribute("alt") || "").toLowerCase();

      if (!alt.trim()) {
        el.style.display = "";
        if (!firstVisibleMedia) firstVisibleMedia = el;
        return;
      }

      const matchedSize = sizes.find((size) => alt.startsWith(size + "-"));

      if (!matchedSize || matchedSize === selectedSize) {
        el.style.display = "";
        if (!firstVisibleMedia) firstVisibleMedia = el;
      } else {
        el.style.display = "none";
      }
    });

    if (shouldScroll && firstVisibleMedia) {
      const container = document.querySelector("[data-box-interval]");
      const intervals = JSON.parse(container.dataset.intervals);

      const intervalOpt = intervals.find((opt) => opt.size == selectedSize && !opt.linked_bundle_id);

      const slideItem = document
        .querySelector(`img[src="${intervalOpt?.product_image_interval}"]`)
        ?.closest(".product__media-item");

      if (!slideItem) {
        return;
      }
      const imageContainer = document.querySelector(".product__media-list");
      if (!imageContainer) return;
      const containerLeft = imageContainer.getBoundingClientRect().left;
      const slideItemLeft = slideItem.getBoundingClientRect().left;
      const scrollTarget = imageContainer.scrollLeft + (slideItemLeft - containerLeft);
      imageContainer.scrollTo({ left: scrollTarget, behavior: "instant" });
    }
  }

  _updateDiscountBlockTop() {
    const discountSpan = document.querySelector(".discount-block-top span");
    if (!discountSpan) return;

    const container = document.querySelector("[data-box-interval]");
    if (!container) return;

    const intervals = JSON.parse(container.dataset.intervals);
    const size = this._getSelectedBundleTotalSize();
    const intervalOpt = intervals.find((opt) => opt.size == size && !opt.linked_bundle_id);
    if (!intervalOpt) return;

    const isSubscription = this._isSellingPlanSelected();

    if (isSubscription) {
      const cheapestVariant = this._getCheapestSelectableVariant();
      const cheapestAdjustedVariant = this._getCheapestAdjustedVariant(this._sellingPlanGroup);
      const { difference } = this._calculateSubscriptionDiscountSum(cheapestVariant, cheapestAdjustedVariant, size);
      if (!difference) return;
      discountSpan.textContent = this._formatMoney(difference);
    } else {
      const bundlePercent = this._getDiscountPercent(size);
      if (!bundlePercent) return;
      const cheapestVariant = this._getCheapestSelectableVariant();
      const unitDiscount = this._floorMoney(this._roundMoney(cheapestVariant.price) * (bundlePercent / 100));
      const savings = unitDiscount * size;
      discountSpan.textContent = this._formatMoney(savings);
    }
  }

  _initStickyBarVisibilityObserver() {
    const totalsBlock = document.querySelector(".skio-bundle-builder__totals");
    const stickyBar = document.querySelector(".skio-bundle-builder__sticky-bar-container");

    if (!totalsBlock || !stickyBar) return;

    const OFFSET_FROM_BOTTOM = 120;
    const builder = stickyBar.closest(".skio-bundle-builder__sticky-container");

    // The bar is fixed to the viewport, so it follows the page for the whole
    // document rather than only while the builder is on screen. It steps aside
    // just for the stretch where the in-page totals block — which carries the
    // same checkout CTA — is on screen, and comes straight back afterwards.
    // Measured from the rects rather than driven by IntersectionObserver:
    // "scrolled past the totals" and "totals still below the fold" are both
    // non-intersecting states, so intersection alone can't tell them apart.
    let hidden = null;
    const applyVisibility = () => {
      const totals = totalsBlock.getBoundingClientRect();
      const shouldHide = totals.bottom > 0 && totals.top <= window.innerHeight - OFFSET_FROM_BOTTOM;

      if (shouldHide === hidden) return;
      hidden = shouldHide;
      stickyBar.classList.toggle("is-hidden", shouldHide);
    };

    window.addEventListener("scroll", applyVisibility, { passive: true });
    window.addEventListener("resize", applyVisibility);

    if (builder) {
      // Reserve the out-of-flow bar's height at the foot of the builder.
      const syncStickyBarHeight = () => {
        builder.style.setProperty("--sticky-bar-height", `${stickyBar.offsetHeight}px`);
        applyVisibility();
      };
      syncStickyBarHeight();
      if (window.ResizeObserver) {
        new ResizeObserver(syncStickyBarHeight).observe(stickyBar);
        // Selecting products reflows the builder without any scroll event.
        new ResizeObserver(applyVisibility).observe(builder);
      }
    }

    applyVisibility();
  }

  _calculateCurrentTotalPrice() {
    const upsellTotal = this._getUpsellTotals();

    const checkedInput = document.querySelector('input[name="bundle-size"]:checked');
    const pickedSize = checkedInput ? parseInt(checkedInput.value) : this._preselectedSize;
    // The double option buys the next sizeInterval up, so both the unit count and the tier move.
    const size = this._getEffectivePricingSize() || pickedSize;

    const cheapestVariant = this._getCheapestSelectableVariant();
    if (!cheapestVariant) return upsellTotal;

    const selectedRadio = document.querySelector('input[name="purchase-option"]:checked');
    const isSubscription = selectedRadio?.dataset.radioType === "selling_plan";

    const discountMultiplier = this._getDiscountMultiplier(size, isSubscription);

    let pricePerUnit;

    if (isSubscription) {
      const cheapestAdjusted = this._getCheapestAdjustedVariant(this._sellingPlanGroup);
      const adjustedPrice =
        cheapestAdjusted?.adjustedPrice > 0 ? cheapestAdjusted.adjustedPrice : cheapestVariant.price;

      pricePerUnit = adjustedPrice * discountMultiplier;
    } else {
      pricePerUnit = cheapestVariant.price * discountMultiplier;
    }

    const bundlePrice = pricePerUnit * size;

    return bundlePrice + upsellTotal;
  }

  _updatePaymentBlock() {
    const el = document.querySelector("[data-payment]");
    if (!el) return;

    const total = this._calculateCurrentTotalPrice();
    const installment = total / 4;

    el.textContent = this._formatMoney(installment);
  }

  _reduceQuantityFromEnd(items, delta) {
    if (delta <= 0) return items;

    const result = items.map((item) => ({ ...item }));

    for (let i = result.length - 1; i >= 0 && delta > 0; i--) {
      const item = result[i];

      if (item.quantity <= delta) {
        delta -= item.quantity;
        item.quantity = 0;
      } else {
        item.quantity -= delta;
        delta = 0;
      }
    }

    return result.filter((item) => item.quantity > 0);
  }

  _initReviewsSlider() {
    const list = document.querySelector(".skio-bundle-builder__reviews-list");
    const dots = document.querySelectorAll(".skio-bundle-builder__reviews-dot");
    if (!list || !dots.length) return;

    const slides = Array.from(list.querySelectorAll(".skio-bundle-builder__review-slide"));

    const getActiveIndex = () => {
      let closest = 0;
      let minDist = Infinity;
      slides.forEach((slide, i) => {
        const dist = Math.abs(slide.offsetLeft - list.scrollLeft);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });
      return closest;
    };

    const setActiveDot = (index) => {
      dots.forEach((dot, i) => dot.classList.toggle("active", i === index));
    };

    list.addEventListener("scroll", () => {
      setActiveDot(getActiveIndex());
    });

    dots.forEach((dot) => {
      dot.addEventListener("click", () => {
        const index = parseInt(dot.dataset.index, 10);
        list.scrollTo({ left: slides[index].offsetLeft, behavior: "smooth" });
      });
    });

    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let hasDragged = false;

    list.addEventListener("mousedown", (e) => {
      isDragging = true;
      hasDragged = false;
      startX = e.pageX;
      startScrollLeft = list.scrollLeft;
      list.style.cursor = "grabbing";
      list.style.userSelect = "none";
      list.style.scrollBehavior = "auto";
      list.style.scrollSnapType = "none";
    });

    list.addEventListener("mousemove", (e) => {
      if (!isDragging) return;
      const delta = e.pageX - startX;
      if (Math.abs(delta) > 5) hasDragged = true;
      list.scrollLeft = startScrollLeft - delta;
    });

    const endDrag = () => {
      if (!isDragging) return;
      isDragging = false;
      list.style.cursor = "";
      list.style.userSelect = "";
      list.style.scrollBehavior = "";
      list.style.scrollSnapType = "";
      if (hasDragged) {
        list.scrollTo({ left: slides[getActiveIndex()].offsetLeft, behavior: "smooth" });
      }
    };

    list.addEventListener("mouseup", endDrag);
    list.addEventListener("mouseleave", endDrag);
  }

  _removeAllListeners() {
    this._listeners.forEach(({ el, type, fn }) => {
      el.removeEventListener(type, fn);
    });
    this._listeners = [];

    if (this._cartWatcherInterval) {
      clearInterval(this._cartWatcherInterval);
    }

    if (this._cartGiftObserver) {
      this._cartGiftObserver.disconnect();
    }

    if (this._patchedFetch && window.fetch === this._patchedFetch) {
      window.fetch = this._patchedFetch._original || window.fetch;
    }

    if (this._drawerOpenObserver) {
      this._drawerOpenObserver.disconnect();
    }
  }
}

const bundleBlock = document.querySelector(".skio-bundle-builder__categories-wrapper");

let bundleInstance = null;

if (bundleBlock) {
  const bundleId = bundleBlock.dataset.bundleId;

  bundleInstance = new BundleBox("https://api.skio.com/storefront-http/get-dynamic-box", bundleId);
}

window.addEventListener("beforeunload", () => {
  bundleInstance.removeAllListeners();
  bundleInstance = null;
});
